import React, { useEffect, useMemo, useState } from "react";
import { ColumnDef, PaginationState } from "@tanstack/react-table";
import { useLocation, useNavigate } from "react-router-dom";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { 
  Loader, 
  MoreVertical, 
  PlusCircle, 
  Calendar as CalendarIcon, 
  HandCoins, 
  Check,
  DollarSign, 
  CheckCircle, 
  Clock,
  Filter,
  Download,
  Upload,
  BarChart3,
  Target,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
  Settings,
  Banknote,
  CreditCard,
  Smartphone,
  Receipt,
  Copy,
  GraduationCap,
  Building,
  Printer,
  Search,
  X,
  Users,
  UserCheck,
  Landmark,
  AlertCircle,
  ArrowUpRight,
  TrendingUp,
  Save,
  ShieldCheck,
  ChevronDown,
  FileSpreadsheet,
  FileDown,
  FileUp,
  ArrowLeft,
  CheckSquare,
  Square,
  AlertTriangle,
  ListChecks,
  Megaphone,
  CalendarDays,
  LayoutList,
  CircleUserRound,
  CheckCheck,
  ChevronRight,
  ChevronLeft,
  StepForward,
  LockIcon,
  PieChart,
  TrendingDown,
  Calendar as CalendarIcon2,
  Grid,
  List,
  Plus,
  Minus,
  Activity,
  Users as Users2,
  UserCheck as UserCheck2,
  Landmark as Landmark2,
  AlertCircle as AlertCircle2,
  ArrowUpRight as ArrowUpRight2,
  TrendingUp as TrendingUp2,
  TrendingDown as TrendingDown2,
  Calendar as CalendarIcon3,
  Grid as Grid2,
  List as List2,
  Plus as Plus2,
  Minus as Minus2,
  Activity as Activity2,
  Users as Users3,
  UserCheck as UserCheck3,
  Landmark as Landmark3,
} from "lucide-react";
import * as XLSX from 'xlsx';
import { useQuery } from "react-query";
import { getCooperativeLogoSrc } from "@/components/ui/cooperative-logo";

const parseExcelDate = (value: unknown): Date | null => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);
    return parsed ? new Date(parsed.y, parsed.m - 1, parsed.d) : null;
  }

  const text = String(value ?? "").trim();
  if (!text) return null;

  const dateOnly = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime())
    ? null
    : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const formatExcelDate = (value: unknown): string => {
  const date = parseExcelDate(value);
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import DataTable from "@/components/datatable/Datatable";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useModalState from "@/hooks/useModalState";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { z } from "zod";
import useConfirmModal from "@/hooks/useConfirmModal";
import ConfirmModal from "@/components/modal/ConfirmModal";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Command,
  CommandInput,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell } from "recharts";
import MonthlyDeclarationControlCenter from "@/components/contributions/MonthlyDeclarationControlCenter";

// Contribution form schema
const contributionFormSchema = z.object({
  teacherId: z.number().min(1, "Member is required"),
  contributionType: z.enum(["Savings", "Shares", "Registration Fee", "Fine", "Loan Repayment", "Other"], {
    required_error: "Contribution type is required",
  }),
  amount: z.number().min(1, "Amount must be greater than 0"),
  shareCount: z.number().optional(),
  paymentMethod: z.enum(["Cash", "Bank Transfer", "Mobile Money", "Check"], {
    required_error: "Payment method is required",
  }),
  transactionRef: z.string().optional(),
  contributionDate: z.date(),
  notes: z.string().optional(),
  isVerified: z.boolean(),
});

type ContributionFormData = z.infer<typeof contributionFormSchema>;

type MonthlyMemberGroup = {
  member: any;
  contributions: any[];
  totalAmount: number;
  count: number;
  verifiedAmount: number;
  pendingAmount: number;
};

function ContributionForm({ isOpen, setIsOpen, refetch, record, startInExcelMode = false }) {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("single");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [bulkMembers, setBulkMembers] = useState<any[]>([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const form = useForm<ContributionFormData>({
    resolver: zodResolver(contributionFormSchema),
    defaultValues: {
      teacherId: 0,
      contributionType: "Savings",
      amount: 0,
      shareCount: 0,
      contributionDate: new Date(),
      notes: "",
      paymentMethod: "Cash",
      transactionRef: "",
      isVerified: false,
    },
  });

  // Safe field mapper — supports multiple naming conventions
  const memberField = (obj: any, ...keys: string[]) => {
    if (!obj) return "";
    for (const key of keys) {
      const v = obj[key];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return "";
  };

  const resolveFullName = (m: any) => {
    return memberField(m, "fullName", "full_names", "fullNames") || 
      `${memberField(m, "firstName", "first_name")} ${memberField(m, "lastName", "last_name")}`.trim() || "Unknown";
  };

  const resolveMemberCode = (m: any) => memberField(m, "memberCode", "member_code", "code");
  const resolvePhone = (m: any) => memberField(m, "phone", "phoneNumber", "phone_number");
  const resolveRole = (m: any) => memberField(m, "role", "memberRole", "member_role");
  const resolveSavings = (m: any) => Number(memberField(m, "currentSavings", "savings", "totalSavings", "savingsAmount")) || 0;
  const resolveShares = (m: any) => Number(memberField(m, "sharesTotalAmount", "sharesBought", "sharesOwned", "totalSharesBought")) || 0;
  const resolveOutstanding = (m: any) => Number(memberField(m, "unpaidLoansAndFines", "outstandingBalance", "outstanding")) || 0;
  const resolveStatus = (m: any) => memberField(m, "membershipStatus", "status") || "Active";
  const resolveDistrict = (m: any) => m?.branch?.district?.name || memberField(m, "district", "districtName", "district_name") || "N/A";

  // Fetch members list
  const membersQuery = useQuery(
    ["members-for-contribution"],
    async () => {
      const { data } = await api.get("/members", { params: { limit: 500 } });
      const items = data?.results || data?.data || data || [];
      return items.map((m: any) => ({
        ...m,
        _fullName: resolveFullName(m),
        _memberCode: resolveMemberCode(m),
        _phone: resolvePhone(m),
        _role: resolveRole(m),
        _savings: resolveSavings(m),
        _shares: resolveShares(m),
        _outstanding: resolveOutstanding(m),
        _status: resolveStatus(m),
        _district: resolveDistrict(m),
      }));
    },
    { enabled: isOpen }
  );

  // Filtered & searched list
  const filteredMembers = useMemo(() => {
    const all = membersQuery.data || [];
    if (!debouncedSearch) return all;
    const q = debouncedSearch.toLowerCase();
    return all.filter(
      (m: any) =>
        m._fullName.toLowerCase().includes(q) ||
        m._memberCode.toLowerCase().includes(q) ||
        m._phone.toLowerCase().includes(q) ||
        (m.idNumber || "").toLowerCase().includes(q)
    );
  }, [debouncedSearch, membersQuery.data]);

  // Selected member details
  const memberDetailsQuery = useQuery(
    ["member-details-form", selectedMember?.id],
    async () => {
      if (!selectedMember?.id) return null;
      try {
        const { data } = await api.get(`/members/${selectedMember.id}`);
        return data?.data || data;
      } catch {
        return null;
      }
    },
    { enabled: !!selectedMember?.id && isOpen, retry: false }
  );

  // Member contribution history (last 5) — errors don't block
  const memberContribHistory = useQuery(
    ["member-contrib-history-form", selectedMember?.id],
    async () => {
      if (!selectedMember?.id) return [];
      const { data } = await api.get(`/contributions/teacher/${selectedMember.id}`, { params: { limit: 5 } });
      return data?.data || [];
    },
    { enabled: !!selectedMember?.id && isOpen, retry: false }
  );

  // Fetch all contributions for stats/charts
  const contributionsQuery = useQuery(
    ["all-contributions"],
    async () => {
      const { data } = await api.get("/contributions", { params: { limit: 10000 } });
      return data;
    }
  );

  const contributionAmount = form.watch("amount");
  const contributionType = form.watch("contributionType");

  const currentSavings = selectedMember?._savings || memberDetailsQuery.data?.currentSavings || 0;
  const sharesOwned = selectedMember?._shares || memberDetailsQuery.data?.sharesTotalAmount || 0;
  const outstandingBalance = selectedMember?._outstanding || memberDetailsQuery.data?.unpaidLoansAndFines || 0;
  const newBalance = currentSavings + (Number(contributionAmount) || 0);

  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState<number>(new Date().getMonth() + 1);
  const [memberFilter, setMemberFilter] = useState<string>('');
  const [contributionTypeFilter, setContributionTypeFilter] = useState<string>('');

  // Available years for filters
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    contributionsQuery.data?.data?.forEach(c => {
      const year = new Date(c.contributionDate).getFullYear();
      years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [contributionsQuery.data?.data]);

  // Savings trend data (last 12 months)
  const savingsTrendData = useMemo(() => {
    const months: any[] = [];
    const now = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const monthDate = subMonths(now, i);
      const monthName = monthDate.toLocaleString('default', { month: 'short' });
      const year = monthDate.getFullYear();
      
      // Filter contributions for this month
      const monthContributions = contributionsQuery.data?.data?.filter(c => {
        const contributionDate = new Date(c.contributionDate);
        return contributionDate.getFullYear() === year && 
               contributionDate.getMonth() === monthDate.getMonth();
      }) || [];
      
      const totalAmount = monthContributions.reduce((sum, c) => sum + (c.amount || 0), 0);
      
      months.push({
        month: monthName,
        total: totalAmount,
        contributions: monthContributions.length,
      });
    }
    
    return months;
  }, [contributionsQuery.data?.data]);

  const availableMonths = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const selectMember = (member: any) => {
    setSelectedMember(member);
    form.setValue("teacherId", member.id);
    setSearchQuery(member._fullName);
  };

  const clearMember = () => {
    setSelectedMember(null);
    setSearchQuery("");
    form.setValue("teacherId", 0);
  };

  const onSubmit = async (data: ContributionFormData) => {
    setIsLoading(true);
    try {
      await api.post("/contributions", data);
      toast.success("Contribution recorded successfully");
      setIsOpen(false);
      refetch();
      form.reset();
      clearMember();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save contribution");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecordAndVerify = async () => {
    const data = form.getValues();
    setIsLoading(true);
    try {
      await api.post("/contributions", { ...data, isVerified: true, status: "Verified" });
      toast.success("Contribution recorded and verified");
      setIsOpen(false);
      refetch();
      form.reset();
      clearMember();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to record contribution");
    } finally {
      setIsLoading(false);
    }
  };

  // Skeleton card
  const SkeletonCard = () => (
    <div className="flex items-center gap-3 p-3 border-b border-gray-100 animate-pulse">
      <div className="h-9 w-9 rounded-full bg-gray-200 shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 w-32 bg-gray-200 rounded" />
        <div className="h-3 w-24 bg-gray-100 rounded" />
      </div>
      <div className="h-5 w-14 bg-gray-100 rounded-full" />
    </div>
  );

  // ─── Bulk Contribution State ──────────────────────────────────────────
  const [bulkMode, setBulkMode] = useState<'select' | 'manual' | 'excel'>('select');
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<number>>(new Set());
  const [bulkSearchQuery, setBulkSearchQuery] = useState('');
  const [bulkDebouncedSearch, setBulkDebouncedSearch] = useState('');
  const [bulkContribType, setBulkContribType] = useState("Savings");
  const [bulkAmountPerMember, setBulkAmountPerMember] = useState<number>(0);
  const [bulkShareCount, setBulkShareCount] = useState<number>(0);
  const [bulkPaymentMethod, setBulkPaymentMethod] = useState("Cash");
  const [bulkContribDate, setBulkContribDate] = useState<Date>(new Date());
  const [bulkTxnRefPrefix, setBulkTxnRefPrefix] = useState("");
  const [bulkNotes, setBulkNotes] = useState("");
  const [bulkShowPreview, setBulkShowPreview] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Excel state
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelParsedRows, setExcelParsedRows] = useState<any[]>([]);
  const [validationResults, setValidationResults] = useState<{ valid: any[]; invalid: any[] }>({ valid: [], invalid: [] });

  useEffect(() => {
    if (isOpen && startInExcelMode) {
      setActiveTab("bulk");
      setBulkMode("excel");
    }
  }, [isOpen, startInExcelMode]);

  // Bulk search debounce
  useEffect(() => {
    const t = setTimeout(() => setBulkDebouncedSearch(bulkSearchQuery), 300);
    return () => clearTimeout(t);
  }, [bulkSearchQuery]);

  // Filtered members for bulk mode
  const bulkFilteredMembers = useMemo(() => {
    const all = membersQuery.data || [];
    if (!bulkDebouncedSearch) return all;
    const q = bulkDebouncedSearch.toLowerCase();
    return all.filter(
      (m: any) =>
        m._fullName.toLowerCase().includes(q) ||
        m._memberCode.toLowerCase().includes(q) ||
        m._phone.toLowerCase().includes(q) ||
        (m.idNumber || "").toLowerCase().includes(q)
    );
  }, [bulkDebouncedSearch, membersQuery.data]);

  // Full selected member objects
  const bulkSelectedMembers = useMemo(() => {
    const all = membersQuery.data || [];
    return all.filter((m: any) => bulkSelectedIds.has(m.id));
  }, [bulkSelectedIds, membersQuery.data]);

  // Preview entries derived from selection + form
  const bulkPreviewEntries = useMemo(() => {
    return bulkSelectedMembers.map((m: any, idx: number) => {
      const ref = bulkTxnRefPrefix
        ? `${bulkTxnRefPrefix}-${String(idx + 1).padStart(3, '0')}`
        : "";
      return {
        memberId: m.id,
        memberCode: m._memberCode,
        email: m.email,
        memberName: m._fullName,
        currentSavings: m._savings,
        amount: Number(bulkAmountPerMember) || 0,
        shares: bulkContribType === 'Shares' ? Number(bulkShareCount) || 0 : 0,
        newTotal: m._savings + (Number(bulkAmountPerMember) || 0),
        transactionReference: ref,
        status: 'Ready',
      };
    });
  }, [bulkSelectedMembers, bulkAmountPerMember, bulkShareCount, bulkContribType, bulkTxnRefPrefix]);

  // Toggle a single member for bulk
  const toggleBulkMember = (id: number) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Select / deselect all visible
  const selectAllBulk = () => setBulkSelectedIds(new Set(bulkFilteredMembers.map((m: any) => m.id)));
  const clearBulkSelection = () => setBulkSelectedIds(new Set());

  // Reset everything bulk
  const resetBulk = () => {
    setBulkMode('select');
    setBulkSelectedIds(new Set());
    setBulkSearchQuery('');
    setBulkDebouncedSearch('');
    setBulkContribType("Savings");
    setBulkAmountPerMember(0);
    setBulkShareCount(0);
    setBulkPaymentMethod("Cash");
    setBulkContribDate(new Date());
    setBulkTxnRefPrefix("");
    setBulkNotes("");
    setBulkShowPreview(false);
    setExcelFile(null);
    setExcelParsedRows([]);
    setValidationResults({ valid: [], invalid: [] });
    setBulkLoading(false);
  };

  // Submit bulk contributions with fallback to individual creates
  const submitBulkEntries = async (entries: any[], contribType: string, paymentMethod: string, date: Date, notes: string, verified: boolean) => {
    if (entries.length === 0) {
      toast.error("No valid entries to submit");
      return;
    }

    const invalidEntries = entries.filter((e) => !e.memberId || !e.amount || Number(e.amount) <= 0);
    if (invalidEntries.length > 0) {
      const names = invalidEntries.map((e) => e.memberName || e.memberCode || `ID:${e.memberId}`).join(", ");
      toast.error(`Invalid entries: ${names} — each entry needs a member and a valid amount`);
      return;
    }

    if (!contribType) {
      toast.error("Contribution type is required");
      return;
    }

    setBulkLoading(true);
    try {
      const payload = {
        contributionType: contribType,
        paymentMethod: paymentMethod,
        contributionDate: date,
        notes: notes || "",
        verifyImmediately: verified,
        ...(verified ? { status: "Verified" } : {}),
        entries: entries.map((e) => ({
          memberId: e.memberId,
          memberCode: e.memberCode || "",
          email: e.email || "",
          memberName: e.memberName || "",
          amount: Number(e.amount) || 0,
          shares: Number(e.shares) || 0,
          transactionReference: e.transactionReference || e.reference || "",
          contributionType: e.contributionType || contribType,
          paymentMethod: e.paymentMethod || paymentMethod,
          contributionDate: formatExcelDate(e.contributionDate || date),
          salaryMonth: formatExcelDate(e.salaryMonth || e.contributionDate || date),
          notes: e.notes || notes || "",
        })),
      };

      const { data } = await api.post("/contributions/bulk", payload);
      if (data.failed > 0) {
        const errorLines = data.errors.map((err: any) => `${err.memberName}: ${err.reason}`).join("\n");
        toast.error(
          `${data.message}\n\nFailed members:\n${errorLines}`,
          { duration: 10000 }
        );
      } else {
        const duplicateSuffix = data.duplicates > 0 ? `; ${data.duplicates} duplicate(s) skipped` : "";
        toast.success(`${data.created ?? data.succeeded} contribution(s) ${verified ? "recorded & verified" : "recorded"}${duplicateSuffix}`);
        setIsOpen(false);
        refetch();
        resetBulk();
      }
    } catch (err: any) {
      const responseData = err?.response?.data;

      if (responseData?.errors && responseData.errors.length > 0) {
        const errorLines = responseData.errors.map((e: any) => `${e.memberName || `Member #${e.memberId}`}: ${e.reason}`).join("\n");
        toast.error(
          `${responseData.message || "Bulk contribution failed"}\n\nFailed members:\n${errorLines}`,
          { duration: 10000 }
        );
      } else {
        const msg = responseData?.message || "";
        if (msg.includes("not found") || msg.includes("Route") || err?.response?.status === 404) {
          toast.error("Bulk endpoint is not available. Try recording contributions individually.");
        } else if (msg) {
          toast.error(msg);
        } else {
          toast.error(`Failed to ${verified ? "record & verify" : "save"} bulk contributions`);
        }
      }
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkSubmit = async (verified: boolean) => {
    if (bulkPreviewEntries.length === 0) {
      toast.error("No entries to submit");
      return;
    }
    await submitBulkEntries(bulkPreviewEntries, bulkContribType, bulkPaymentMethod, bulkContribDate, bulkNotes, verified);
  };

  // ─── Excel helpers ────────────────────────────────────────────────────
  const downloadExcelTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Member Code", "Contribution Type", "Amount", "Shares", "Payment Method", "Contribution Date", "Salary Month", "Reference", "Notes"],
      ["M-001", "Savings", 50000, "", "Cash", "2026-06-10", "2026-06-01", "TXN-001", ""],
    ]);
    ws["!cols"] = [
      { wch: 18 }, { wch: 20 }, { wch: 14 }, { wch: 10 }, { wch: 18 },
      { wch: 20 }, { wch: 16 }, { wch: 18 }, { wch: 30 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "bulk_contribution_template.xlsx");
  };

  const handleExcelUpload = (file: File) => {
    setExcelFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target?.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(ws, { defval: "", raw: true });
        setExcelParsedRows(rows);
        setValidationResults({ valid: [], invalid: [] });
        toast.success(`Parsed ${rows.length} rows from Excel`);
      } catch {
        toast.error("Failed to parse Excel file. Check the format.");
      }
    };
    reader.readAsBinaryString(file);
  };

  const validateExcelRows = () => {
    const membersMap = new Map<string, any>();
    (membersQuery.data || []).forEach((m: any) => membersMap.set(m._memberCode?.toLowerCase(), m));

    const valid: any[] = [];
    const invalid: any[] = [];
    const seenRows = new Set<string>();
    let duplicateCount = 0;
    const validPaymentMethods = ["Cash", "Bank Transfer", "Mobile Money", "Check", "Salary Deduction"];
    const validContribTypes = ["Savings", "Shares", "Registration Fee", "Fine", "Loan Repayment", "Other", "Monthly", "Weekly", "One-time"];

    excelParsedRows.forEach((row: any, idx: number) => {
      const errors: string[] = [];
      const memberCode = String(row["Member Code"] || row["memberCode"] || row["MemberCode"] || "").trim();
      const contribType = String(row["Contribution Type"] || row["contributionType"] || row["ContributionType"] || "").trim();
      const amountRaw = row["Amount"] ?? row["amount"] ?? "";
      const sharesRaw = row["Shares"] ?? row["shares"] ?? "";
      const paymentMethod = String(row["Payment Method"] || row["paymentMethod"] || row["PaymentMethod"] || "").trim();
      const dateRaw = row["Contribution Date"] || row["contributionDate"] || row["ContributionDate"] || "";
      const salaryMonthRaw = row["Salary Month"] || row["salaryMonth"] || row["SalaryMonth"] || dateRaw;
      const reference = String(row["Reference"] || row["transactionReference"] || row["TransactionReference"] || "").trim();
      const notes = String(row["Notes"] || row["notes"] || "").trim();
      const contributionDate = parseExcelDate(dateRaw);
      const salaryMonth = parseExcelDate(salaryMonthRaw);

      if (!memberCode) errors.push("Missing member code");
      const member = membersMap.get(memberCode.toLowerCase());
      if (!member && memberCode) errors.push("Invalid member code");
      if (!contribType) errors.push("Missing contribution type");
      if (contribType && !validContribTypes.includes(contribType)) errors.push(`Unsupported contribution type: ${contribType}`);
      const amount = Number(amountRaw);
      if (!amountRaw && amountRaw !== 0) errors.push("Missing amount");
      else if (isNaN(amount) || amount <= 0) errors.push("Invalid amount");
      if (paymentMethod && !validPaymentMethods.includes(paymentMethod)) errors.push(`Unsupported payment method: ${paymentMethod}`);
      if (!dateRaw) errors.push("Missing contribution date");
      else if (!contributionDate) errors.push("Invalid contribution date");
      if (!salaryMonth) errors.push("Invalid salary month");

      const duplicateKey = [
        memberCode.toLowerCase(),
        contribType.toLowerCase(),
        amount,
        Number(sharesRaw) || 0,
        paymentMethod.toLowerCase(),
        formatExcelDate(contributionDate),
        formatExcelDate(salaryMonth),
        reference.toLowerCase(),
      ].join("|");

      if (seenRows.has(duplicateKey)) {
        duplicateCount += 1;
        return;
      }
      seenRows.add(duplicateKey);

      const entry = {
        rowNumber: idx + 2,
        memberCode,
        email: member?.email || "",
        memberName: member?._fullName || "Unknown",
        currentSavings: member?._savings || 0,
        contributionType: contribType || "Savings",
        amount,
        shares: Number(sharesRaw) || 0,
        paymentMethod: paymentMethod || "Cash",
        contributionDate: contributionDate || new Date(),
        salaryMonth: salaryMonth || contributionDate || new Date(),
        reference,
        notes,
        memberId: member?.id || 0,
        newTotal: (member?._savings || 0) + amount,
      };

      if (errors.length > 0) {
        invalid.push({ ...entry, errors });
      } else {
        valid.push(entry);
      }
    });

    setValidationResults({ valid, invalid });
    if (duplicateCount > 0) toast.info(`${duplicateCount} duplicate row(s) removed`);
    if (invalid.length > 0) toast.error(`${invalid.length} row(s) have validation errors`);
    if (valid.length > 0) toast.success(`${valid.length} row(s) are valid`);
  };

  return (
    <Sheet open={isOpen} onOpenChange={(e) => { setIsOpen(e); if (!e) { clearMember(); resetBulk(); } }}>
      <SheetContent side="right" className="w-full sm:max-w-[85vw] p-0 flex flex-col">
        <SheetHeader className="sticky top-0 z-20 bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <HandCoins className="h-5 w-5 text-[#0A9F43]" />
              <SheetTitle className="text-lg font-bold text-gray-900">Member Contribution Management</SheetTitle>
            </div>
            <button onClick={() => { setIsOpen(false); clearMember(); }} className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-gray-100 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 overflow-hidden">
          <div className="border-b px-6 py-2 bg-gray-50">
            <TabsList className="bg-white border">
              <TabsTrigger value="single" className="text-sm data-[state=active]:bg-[#0A9F43] data-[state=active]:text-white">Single Member</TabsTrigger>
              <TabsTrigger value="bulk" className="text-sm data-[state=active]:bg-[#0A9F43] data-[state=active]:text-white">Bulk Contribution</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="single" className="flex-1 overflow-hidden p-0 m-0">
            <div className="flex h-full">
              {/* LEFT PANEL — 340px */}
              <div className="w-[340px] min-w-[340px] border-r bg-white flex flex-col overflow-hidden">
                {/* Sticky search */}
                <div className="sticky top-0 z-10 bg-white border-b px-3 py-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by name, code, phone, ID..."
                      className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A9F43]/20 focus:border-[#0A9F43] bg-gray-50"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2">
                        <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Scrollable member list */}
                <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
                  {/* Loading state */}
                  {membersQuery.isLoading && (
                    <div>
                      {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
                    </div>
                  )}

                  {/* Error state */}
                  {membersQuery.isError && (
                    <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                      <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center mb-3">
                        <X className="h-6 w-6 text-red-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Failed to load members</p>
                      <p className="text-xs text-gray-400 mb-4">Could not fetch member list</p>
                      <Button size="sm" variant="outline" onClick={() => membersQuery.refetch()}>
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                        Retry
                      </Button>
                    </div>
                  )}

                  {/* Empty */}
                  {!membersQuery.isLoading && !membersQuery.isError && filteredMembers.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                      <Users className="h-10 w-10 text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">
                        {debouncedSearch ? "No members found" : "No members available"}
                      </p>
                      {debouncedSearch && (
                        <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
                      )}
                    </div>
                  )}

                  {/* Member cards */}
                  {!membersQuery.isLoading && !membersQuery.isError && filteredMembers.map((m: any) => {
                    const isSelected = selectedMember?.id === m.id;
                    return (
                      <button
                        key={m.id}
                        onClick={() => selectMember(m)}
                        className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors hover:bg-gray-50 ${
                          isSelected ? "bg-[#0A9F43]/5 ring-1 ring-[#0A9F43]/20" : ""
                        }`}
                      >
                        <Avatar className="h-9 w-9 shrink-0">
                          <AvatarFallback className={`text-xs font-medium ${isSelected ? "bg-[#0A9F43] text-white" : "bg-gray-100 text-gray-500"}`}>
                            {m._fullName.split(" ").map((s: string) => s[0]).filter(Boolean).slice(0, 2).join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isSelected ? "text-[#0A9F43]" : "text-gray-900"}`}>
                            {m._fullName}
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            {m._memberCode && <span className="font-mono">{m._memberCode}</span>}
                            {m._memberCode && m._phone && <span className="mx-1">•</span>}
                            {m._phone && <span>{m._phone}</span>}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[11px] text-gray-400">{m._role}</span>
                            <span className="text-[11px] font-medium text-[#0A9F43]">{m._savings.toLocaleString()} FRW</span>
                          </div>
                        </div>
                        <span className={`shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                          m._status === "Active" || m._status === "active"
                            ? "bg-green-50 text-green-700"
                            : "bg-gray-100 text-gray-500"
                        }`}>
                          {m._status}
                        </span>
                        {isSelected && (
                          <div className="w-1 h-full absolute right-0 top-0 bg-[#0A9F43] rounded-l" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className="flex-1 overflow-y-auto bg-gray-50/30">
                {selectedMember ? (
                  <div className="p-6 space-y-6 max-w-2xl">
                    {/* Member Summary */}
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                      <div className="bg-[#0A9F43] px-5 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 border-2 border-white/40">
                            <AvatarFallback className="bg-white/20 text-white text-sm font-bold">
                              {selectedMember._fullName.split(" ").map((s: string) => s[0]).filter(Boolean).slice(0, 2).join("")}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-white">
                            <p className="font-bold text-base">{selectedMember._fullName}</p>
                            <p className="text-xs text-white/80">{selectedMember._memberCode || `ID: ${selectedMember.id}`}</p>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 grid grid-cols-2 gap-y-2.5 gap-x-6 text-sm">
                        <div className="flex justify-between"><span className="text-gray-500">Phone</span><span className="font-medium">{selectedMember._phone || "N/A"}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Role</span><span className="font-medium">{selectedMember._role || "Member"}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">District</span><span className="font-medium">{selectedMember._district}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Status</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            selectedMember._status === "Active" || selectedMember._status === "active"
                              ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                          }`}>{selectedMember._status}</span>
                        </div>
                      </div>
                      <div className="border-t grid grid-cols-3 divide-x">
                        <div className="p-3 text-center"><p className="text-[11px] text-gray-500">Current Savings</p><p className="text-sm font-bold text-[#0A9F43]">{currentSavings.toLocaleString()} FRW</p></div>
                        <div className="p-3 text-center"><p className="text-[11px] text-gray-500">Shares Owned</p><p className="text-sm font-bold text-blue-600">{sharesOwned.toLocaleString()} FRW</p></div>
                        <div className="p-3 text-center"><p className="text-[11px] text-gray-500">Outstanding</p><p className="text-sm font-bold text-orange-600">{outstandingBalance.toLocaleString()} FRW</p></div>
                      </div>
                    </div>

                    {/* Contribution History */}
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                      <div className="px-4 py-2.5 border-b bg-gray-50 flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-700">Contribution History</p>
                        <span className="text-xs text-gray-400">Last 5</span>
                      </div>
                      {memberContribHistory.isLoading ? (
                        <div className="p-4 text-center"><Loader className="h-5 w-5 animate-spin mx-auto text-gray-300" /></div>
                      ) : memberContribHistory.isError ? (
                        <div className="p-4 text-center text-xs text-gray-400">Could not load history</div>
                      ) : memberContribHistory.data?.length > 0 ? (
                        <div className="divide-y">
                          {memberContribHistory.data.slice(0, 5).map((c: any) => {
                            const cStatus = c.status || (c.isVerified ? "Verified" : "Pending");
                            return (
                              <div key={c.id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className={`h-2 w-2 rounded-full shrink-0 ${
                                    cStatus === "Verified" ? "bg-green-500" : cStatus === "Rejected" ? "bg-red-500" : "bg-amber-400"
                                  }`} />
                                  <div>
                                    <p className="font-medium text-gray-800">{c.contributionType || "Savings"}</p>
                                    <p className="text-xs text-gray-400">{c.contributionDate ? format(new Date(c.contributionDate), "MMM d, yyyy") : "N/A"}</p>
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="font-semibold">{(c.amount || 0).toLocaleString()} FRW</p>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                    cStatus === "Verified" ? "bg-green-50 text-green-700" :
                                    cStatus === "Rejected" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"
                                  }`}>{cStatus}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="p-5 text-center text-sm text-gray-400">
                          No contributions recorded yet
                        </div>
                      )}
                    </div>

                    {/* Contribution Form */}
                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit)} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                        <h4 className="text-sm font-bold text-gray-800 mb-4">Record Contribution</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <FormField control={form.control} name="contributionType" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">Contribution Type *</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl><SelectTrigger className="h-9"><SelectValue /></SelectTrigger></FormControl>
                                <SelectContent>
                                  <SelectItem value="Savings">Savings</SelectItem>
                                  <SelectItem value="Shares">Shares</SelectItem>
                                  <SelectItem value="Registration Fee">Registration Fee</SelectItem>
                                  <SelectItem value="Fine">Fine</SelectItem>
                                  <SelectItem value="Loan Repayment">Loan Repayment</SelectItem>
                                  <SelectItem value="Other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )} />
                          {contributionType === "Shares" && (
                            <FormField control={form.control} name="shareCount" render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-xs">Number of Shares</FormLabel>
                                <FormControl><Input type="number" placeholder="0" className="h-9" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )} />
                          )}
                          {contributionType !== "Shares" && <div />}
                          <FormField control={form.control} name="amount" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">Amount (FRW) *</FormLabel>
                              <FormControl><Input type="number" placeholder="50000" className="h-9" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField control={form.control} name="paymentMethod" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">Payment Method *</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl><SelectTrigger className="h-9"><SelectValue /></SelectTrigger></FormControl>
                                <SelectContent>
                                  <SelectItem value="Cash">Cash</SelectItem>
                                  <SelectItem value="Mobile Money">Mobile Money</SelectItem>
                                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                                  <SelectItem value="Check">Check</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField control={form.control} name="contributionDate" render={({ field }) => (
                            <FormItem className="flex flex-col">
                              <FormLabel className="text-xs">Contribution Date</FormLabel>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button variant="outline" className={cn("h-9 w-full pl-3 text-left font-normal text-sm", !field.value && "text-muted-foreground")}>
                                      {field.value ? format(field.value, "MMM d, yyyy") : <span>Pick a date</span>}
                                      <CalendarIcon className="ml-auto h-3.5 w-3.5 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={field.value} onSelect={field.onChange} initialFocus /></PopoverContent>
                              </Popover>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField control={form.control} name="transactionRef" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">Transaction Reference</FormLabel>
                              <FormControl><Input placeholder="e.g. TXN-001" className="h-9" {...field} /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        </div>
                        <FormField control={form.control} name="notes" render={({ field }) => (
                          <FormItem className="mt-4">
                            <FormLabel className="text-xs">Notes (Optional)</FormLabel>
                            <FormControl><Textarea placeholder="Additional notes..." className="resize-none h-16 text-sm" {...field} /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />

                        {/* Live Preview */}
                        <div className="mt-4 rounded-lg border border-[#0A9F43]/20 bg-gradient-to-br from-[#f0fdf4] to-white p-3">
                          <div className="flex items-center gap-1.5 mb-2">
                            <TrendingUp className="h-3.5 w-3.5 text-[#0A9F43]" />
                            <span className="text-xs font-semibold text-gray-600">Balance Preview</span>
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-gray-500">Current Savings</span><span className="font-medium">{currentSavings.toLocaleString()} FRW</span></div>
                            {Number(contributionAmount) > 0 && (
                              <div className="flex justify-between text-[#0A9F43]"><span>+ New Contribution</span><span className="font-semibold">+ {Number(contributionAmount).toLocaleString()} FRW</span></div>
                            )}
                            <div className="border-t pt-1.5 flex justify-between font-bold text-gray-900"><span>New Total</span><span>{newBalance.toLocaleString()} FRW</span></div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-4 pt-4 border-t flex items-center justify-end gap-2">
                          <Button type="button" variant="outline" size="sm" onClick={() => setIsOpen(false)}>Cancel</Button>
                          <Button type="submit" disabled={isLoading} size="sm" className="bg-gray-700 hover:bg-gray-800 text-white border-0">
                            {isLoading ? <Loader className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                            Record Contribution
                          </Button>
                          <Button type="button" disabled={isLoading} size="sm" onClick={handleRecordAndVerify} className="bg-[#0A9F43] hover:bg-[#0A9F43]/90 text-white border-0 shadow-sm">
                            {isLoading ? <Loader className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />}
                            Record & Verify
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center px-6">
                    <div className="h-20 w-20 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                      <Users className="h-10 w-10 text-gray-300" />
                    </div>
                    <p className="text-base font-semibold text-gray-600">Select a member</p>
                    <p className="text-sm text-gray-400 mt-1 max-w-xs">Choose a member from the left panel to view details and record a contribution</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="bulk" className="flex-1 overflow-hidden p-0 m-0">
            {bulkMode === 'select' ? (
              /* ── Mode Selection ──────────────────────────────────────── */
              <div className="flex items-center justify-center h-full bg-gray-50/30">
                <div className="max-w-xl w-full px-8">
                  <div className="text-center mb-8">
                    <h3 className="text-lg font-bold text-gray-900">Bulk Contribution</h3>
                    <p className="text-sm text-gray-500 mt-1">Record contributions for multiple members at once</p>
                  </div>
                  {membersQuery.isLoading ? (
                    <div className="flex justify-center py-12">
                      <Loader className="h-6 w-6 animate-spin text-[#0A9F43]" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-5">
                      <button
                        onClick={() => setBulkMode('manual')}
                        className="group rounded-xl border-2 border-dashed border-gray-200 p-7 text-center hover:border-[#0A9F43]/50 hover:bg-[#0A9F43]/5 transition-all bg-white"
                      >
                        <div className="h-14 w-14 rounded-xl bg-[#0A9F43]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#0A9F43]/20 transition-colors">
                          <Users className="h-7 w-7 text-[#0A9F43]" />
                        </div>
                        <p className="text-sm font-semibold text-gray-800">Select Members Manually</p>
                        <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">Choose multiple members from the list with checkboxes</p>
                      </button>
                      <button
                        onClick={() => setBulkMode('excel')}
                        className="group rounded-xl border-2 border-dashed border-gray-200 p-7 text-center hover:border-[#0A9F43]/50 hover:bg-[#0A9F43]/5 transition-all bg-white"
                      >
                        <div className="h-14 w-14 rounded-xl bg-emerald-50 flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-100 transition-colors">
                          <FileSpreadsheet className="h-7 w-7 text-emerald-600" />
                        </div>
                        <p className="text-sm font-semibold text-gray-800">Upload Excel Template</p>
                        <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">Download template, fill data, and upload for bulk processing</p>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : bulkMode === 'manual' ? (
              /* ── Manual Mode: Two-Panel Layout ──────────────────────── */
              <div className="flex h-full">
                {/* LEFT PANEL: Member List with Checkboxes */}
                <div className="w-[340px] min-w-[340px] border-r bg-white flex flex-col overflow-hidden">
                  {/* Search */}
                  <div className="sticky top-0 z-10 bg-white border-b px-3 py-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search members..."
                        className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A9F43]/20 focus:border-[#0A9F43] bg-gray-50"
                        value={bulkSearchQuery}
                        onChange={(e) => setBulkSearchQuery(e.target.value)}
                      />
                      {bulkSearchQuery && (
                        <button onClick={() => setBulkSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2">
                          <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                        </button>
                      )}
                    </div>
                    {/* Select All / Clear */}
                    <div className="flex items-center justify-between mt-2 px-0.5">
                      <button onClick={selectAllBulk} className="text-xs font-medium text-[#0A9F43] hover:text-[#0A9F43]/80 transition-colors">
                        Select All Visible
                      </button>
                      <span className="text-xs text-gray-400">({bulkSelectedIds.size} selected)</span>
                      {bulkSelectedIds.size > 0 && (
                        <button onClick={clearBulkSelection} className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors">
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Member list */}
                  <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
                    {membersQuery.isLoading ? (
                      <div>{Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}</div>
                    ) : membersQuery.isError ? (
                      <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                        <AlertCircle className="h-8 w-8 text-red-300 mb-2" />
                        <p className="text-sm text-gray-500 mb-3">Failed to load members</p>
                        <Button size="sm" variant="outline" onClick={() => membersQuery.refetch()}>
                          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
                        </Button>
                      </div>
                    ) : bulkFilteredMembers.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                        <Users className="h-8 w-8 text-gray-300 mb-2" />
                        <p className="text-sm text-gray-400">{bulkDebouncedSearch ? "No members found" : "No members available"}</p>
                      </div>
                    ) : (
                      bulkFilteredMembers.map((m: any) => {
                        const checked = bulkSelectedIds.has(m.id);
                        return (
                          <button
                            key={m.id}
                            onClick={() => toggleBulkMember(m.id)}
                            className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors hover:bg-gray-50 ${checked ? "bg-[#0A9F43]/5" : ""}`}
                          >
                            <div className={`shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${checked ? "bg-[#0A9F43] border-[#0A9F43]" : "border-gray-300"}`}>
                              {checked && <CheckSquare className="h-3 w-3 text-white" />}
                            </div>
                            <Avatar className="h-8 w-8 shrink-0">
                              <AvatarFallback className={`text-[10px] font-medium ${checked ? "bg-[#0A9F43] text-white" : "bg-gray-100 text-gray-500"}`}>
                                {m._fullName.split(" ").map((s: string) => s[0]).filter(Boolean).slice(0, 2).join("")}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${checked ? "text-[#0A9F43]" : "text-gray-900"}`}>{m._fullName}</p>
                              <p className="text-xs text-gray-400 truncate">
                                {m._memberCode && <span className="font-mono">{m._memberCode}</span>}
                                {m._memberCode && m._phone && <span className="mx-1">•</span>}
                                {m._phone && <span>{m._phone}</span>}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-gray-400">{m._role}</span>
                                <span className="text-[10px] font-medium text-[#0A9F43]">{m._savings.toLocaleString()} FRW</span>
                              </div>
                            </div>
                            <span className={`shrink-0 text-[9px] font-medium px-1.5 py-0.5 rounded-full ${m._status === "Active" || m._status === "active" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                              {m._status}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* RIGHT PANEL: Form / Preview */}
                <div className="flex-1 overflow-y-auto bg-gray-50/30">
                  {bulkSelectedIds.size === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center px-6">
                      <div className="h-16 w-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                        <Users className="h-8 w-8 text-gray-300" />
                      </div>
                      <p className="text-sm font-semibold text-gray-600">Select Members</p>
                      <p className="text-xs text-gray-400 mt-1 max-w-xs">Check members from the left panel to begin</p>
                    </div>
                  ) : !bulkShowPreview ? (
                    /* ── Bulk Form Settings ──────────────────────────────── */
                    <div className="p-6 space-y-5 max-w-xl">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-bold text-gray-800">Contribution Settings</h4>
                          <p className="text-xs text-gray-400 mt-0.5">{bulkSelectedIds.size} member{bulkSelectedIds.size > 1 ? 's' : ''} selected</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={clearBulkSelection} className="text-xs h-8">
                          <X className="h-3 w-3 mr-1" /> Clear
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-4 bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1.5 block">Contribution Type *</label>
                          <select
                            value={bulkContribType}
                            onChange={(e) => setBulkContribType(e.target.value)}
                            className="w-full h-9 text-sm border border-gray-200 rounded-lg px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#0A9F43]/20 focus:border-[#0A9F43]"
                          >
                            <option value="Savings">Savings</option>
                            <option value="Shares">Shares</option>
                            <option value="Registration Fee">Registration Fee</option>
                            <option value="Fine">Fine</option>
                            <option value="Loan Repayment">Loan Repayment</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1.5 block">Amount per Member (FRW) *</label>
                          <input
                            type="number"
                            placeholder="50000"
                            value={bulkAmountPerMember || ''}
                            onChange={(e) => setBulkAmountPerMember(Number(e.target.value))}
                            className="w-full h-9 text-sm border border-gray-200 rounded-lg px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#0A9F43]/20 focus:border-[#0A9F43]"
                          />
                        </div>
                        {bulkContribType === "Shares" && (
                          <div>
                            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Number of Shares</label>
                            <input
                              type="number"
                              placeholder="10"
                              value={bulkShareCount || ''}
                              onChange={(e) => setBulkShareCount(Number(e.target.value))}
                              className="w-full h-9 text-sm border border-gray-200 rounded-lg px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#0A9F43]/20 focus:border-[#0A9F43]"
                            />
                          </div>
                        )}
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1.5 block">Payment Method *</label>
                          <select
                            value={bulkPaymentMethod}
                            onChange={(e) => setBulkPaymentMethod(e.target.value)}
                            className="w-full h-9 text-sm border border-gray-200 rounded-lg px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#0A9F43]/20 focus:border-[#0A9F43]"
                          >
                            <option value="Cash">Cash</option>
                            <option value="Mobile Money">Mobile Money</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="Check">Check</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1.5 block">Contribution Date</label>
                          <input
                            type="date"
                            value={format(bulkContribDate, "yyyy-MM-dd")}
                            onChange={(e) => setBulkContribDate(new Date(e.target.value))}
                            className="w-full h-9 text-sm border border-gray-200 rounded-lg px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#0A9F43]/20 focus:border-[#0A9F43]"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1.5 block">Transaction Ref Prefix</label>
                          <input
                            type="text"
                            placeholder="e.g. BULK-"
                            value={bulkTxnRefPrefix}
                            onChange={(e) => setBulkTxnRefPrefix(e.target.value)}
                            className="w-full h-9 text-sm border border-gray-200 rounded-lg px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#0A9F43]/20 focus:border-[#0A9F43]"
                          />
                        </div>
                      </div>

                      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                        <label className="text-xs font-medium text-gray-600 mb-1.5 block">Notes (Optional)</label>
                        <textarea
                          placeholder="Additional notes for all contributions..."
                          value={bulkNotes}
                          onChange={(e) => setBulkNotes(e.target.value)}
                          rows={2}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#0A9F43]/20 focus:border-[#0A9F43] resize-none"
                        />
                      </div>

                      {/* Preview summary */}
                      <div className="rounded-lg border border-[#0A9F43]/20 bg-gradient-to-br from-[#f0fdf4] to-white p-4">
                        <div className="flex items-center gap-1.5 mb-3">
                          <ListChecks className="h-4 w-4 text-[#0A9F43]" />
                          <span className="text-sm font-semibold text-gray-700">Summary</span>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div><span className="text-gray-500">Members</span><p className="font-bold text-gray-800">{bulkSelectedIds.size}</p></div>
                          <div><span className="text-gray-500">Amount Each</span><p className="font-bold text-gray-800">{bulkAmountPerMember.toLocaleString()} FRW</p></div>
                          <div><span className="text-gray-500">Total</span><p className="font-bold text-[#0A9F43]">{(bulkSelectedIds.size * bulkAmountPerMember).toLocaleString()} FRW</p></div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-2">
                        <Button variant="outline" size="sm" onClick={() => { resetBulk(); setActiveTab('single'); }} className="h-9">
                          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
                        </Button>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => setBulkShowPreview(true)}
                            disabled={!bulkAmountPerMember || bulkAmountPerMember <= 0}
                            className="bg-[#0A9F43] hover:bg-[#0A9F43]/90 text-white h-9"
                          >
                            Preview Entries <ArrowLeft className="h-3.5 w-3.5 ml-1.5 rotate-180" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* ── Preview Table ──────────────────────────────────── */
                    <div className="p-6 space-y-5 max-w-2xl">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-bold text-gray-800">Preview Contributions</h4>
                          <p className="text-xs text-gray-400 mt-0.5">{bulkPreviewEntries.length} entr{bulkPreviewEntries.length > 1 ? 'ies' : 'y'} to be recorded</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => setBulkShowPreview(false)} className="text-xs h-8">
                          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Form
                        </Button>
                      </div>

                      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                              <tr>
                                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-600">#</th>
                                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-600">Code</th>
                                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-600">Member</th>
                                <th className="text-right px-3 py-2.5 text-xs font-semibold text-gray-600">Savings</th>
                                <th className="text-right px-3 py-2.5 text-xs font-semibold text-gray-600">Amount</th>
                                <th className="text-right px-3 py-2.5 text-xs font-semibold text-gray-600">New Total</th>
                                <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-600">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {bulkPreviewEntries.map((entry, idx) => (
                                <tr key={entry.memberId} className="hover:bg-gray-50/50">
                                  <td className="px-3 py-2.5 text-xs text-gray-500">{idx + 1}</td>
                                  <td className="px-3 py-2.5 text-xs font-mono font-medium text-gray-700">{entry.memberCode || '—'}</td>
                                  <td className="px-3 py-2.5 text-xs font-medium text-gray-800">{entry.memberName}</td>
                                  <td className="px-3 py-2.5 text-xs text-right text-gray-600">{entry.currentSavings.toLocaleString()}</td>
                                  <td className="px-3 py-2.5 text-xs text-right font-semibold text-[#0A9F43]">+{entry.amount.toLocaleString()}</td>
                                  <td className="px-3 py-2.5 text-xs text-right font-bold text-gray-900">{entry.newTotal.toLocaleString()}</td>
                                  <td className="px-3 py-2.5 text-center">
                                    <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">{entry.status}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Totals */}
                      <div className="rounded-lg border border-[#0A9F43]/20 bg-gradient-to-br from-[#f0fdf4] to-white p-4">
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div><span className="text-gray-500">Total Members</span><p className="font-bold text-gray-800">{bulkPreviewEntries.length}</p></div>
                          <div><span className="text-gray-500">Total Amount</span><p className="font-bold text-gray-800">{bulkPreviewEntries.reduce((s, e) => s + e.amount, 0).toLocaleString()} FRW</p></div>
                          <div><span className="text-gray-500">Total Shares</span><p className="font-bold text-gray-800">{bulkPreviewEntries.reduce((s, e) => s + e.shares, 0)}</p></div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-2">
                        <Button variant="outline" size="sm" onClick={() => setIsOpen(false)} className="h-9">
                          Cancel
                        </Button>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            disabled={bulkLoading}
                            onClick={() => handleBulkSubmit(false)}
                            className="bg-gray-700 hover:bg-gray-800 text-white h-9"
                          >
                            {bulkLoading ? <Loader className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                            Record Bulk
                          </Button>
                          <Button
                            size="sm"
                            disabled={bulkLoading}
                            onClick={() => handleBulkSubmit(true)}
                            className="bg-[#0A9F43] hover:bg-[#0A9F43]/90 text-white h-9 shadow-sm"
                          >
                            {bulkLoading ? <Loader className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
                            Record & Verify
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* ── Excel Mode: Single Panel ────────────────────────────── */
              <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">
                <div className="max-w-2xl mx-auto space-y-5">
                  {/* Back */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-gray-800">Upload Excel Template</h4>
                      <p className="text-xs text-gray-400 mt-0.5">Download template, fill member data, and upload</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => resetBulk()} className="text-xs h-8">
                      <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Choose Mode
                    </Button>
                  </div>

                  {/* Download Template */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">Step 1: Download Template</p>
                        <p className="text-xs text-gray-400 mt-0.5">Excel file with the required columns</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={downloadExcelTemplate}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                      >
                        <FileDown className="h-4 w-4 mr-1.5" /> Download
                      </Button>
                    </div>
                  </div>

                  {/* Upload */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <p className="text-sm font-semibold text-gray-800 mb-1">Step 2: Upload Filled Template</p>
                    <p className="text-xs text-gray-400 mb-4">File must be .xlsx or .xls format</p>
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${excelFile ? "border-[#0A9F43]/50 bg-[#0A9F43]/5" : "border-gray-200 hover:border-[#0A9F43]/50"}`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (file) handleExcelUpload(file);
                      }}
                    >
                      {!excelFile ? (
                        <>
                          <FileUp className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                          <p className="text-sm text-gray-500 mb-3">Drag & drop your Excel file here, or click to browse</p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = '.xlsx,.xls';
                              input.onchange = (e: any) => {
                                if (e.target.files[0]) handleExcelUpload(e.target.files[0]);
                              };
                              input.click();
                            }}
                            className="h-9"
                          >
                            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Browse Files
                          </Button>
                        </>
                      ) : (
                        <div>
                          <div className="flex items-center justify-center gap-3 mb-3">
                            <FileSpreadsheet className="h-8 w-8 text-[#0A9F43]" />
                            <div className="text-left">
                              <p className="text-sm font-medium text-gray-800">{excelFile.name}</p>
                              <p className="text-xs text-gray-400">{(excelFile.size / 1024).toFixed(1)} KB • {excelParsedRows.length} rows parsed</p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setExcelFile(null); setExcelParsedRows([]); setValidationResults({ valid: [], invalid: [] }); }}
                              className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="flex items-center justify-center gap-2 mt-3">
                            <Button size="sm" variant="outline" onClick={() => { setExcelFile(null); setExcelParsedRows([]); setValidationResults({ valid: [], invalid: [] }); }} className="h-9">
                              Re-upload
                            </Button>
                            {excelParsedRows.length > 0 && (
                              <Button size="sm" onClick={validateExcelRows} className="bg-[#0A9F43] hover:bg-[#0A9F43]/90 text-white h-9">
                                <CheckCircle className="h-4 w-4 mr-1.5" /> Validate Data
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Validation Results */}
                  {(validationResults.valid.length > 0 || validationResults.invalid.length > 0) && (
                    <div className="space-y-4">
                      {/* Valid rows */}
                      {validationResults.valid.length > 0 && (
                        <div className="bg-white rounded-xl border border-green-200 overflow-hidden shadow-sm">
                          <div className="px-4 py-2.5 bg-green-50 border-b border-green-100 flex items-center justify-between">
                            <p className="text-sm font-semibold text-green-800">
                              <CheckCircle className="h-4 w-4 inline mr-1.5" />
                              Valid Rows ({validationResults.valid.length})
                            </p>
                          </div>
                          <div className="overflow-x-auto max-h-60 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">#</th>
                                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Code</th>
                                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Name</th>
                                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600">Amount</th>
                                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-600">Type</th>
                                  <th className="text-center px-3 py-2 text-xs font-semibold text-gray-600">Month</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {validationResults.valid.map((r: any, idx: number) => (
                                  <tr key={idx} className="hover:bg-gray-50/50">
                                    <td className="px-3 py-2 text-xs text-gray-500">{r.rowNumber}</td>
                                    <td className="px-3 py-2 text-xs font-mono text-gray-700">{r.memberCode}</td>
                                    <td className="px-3 py-2 text-xs text-gray-800">{r.memberName}</td>
                                    <td className="px-3 py-2 text-xs text-right font-medium text-gray-800">{r.amount.toLocaleString()}</td>
                                    <td className="px-3 py-2 text-center"><span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">{r.contributionType}</span></td>
                                    <td className="px-3 py-2 text-center text-xs text-gray-600">{formatExcelDate(r.salaryMonth)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Invalid rows */}
                      {validationResults.invalid.length > 0 && (
                        <div className="bg-white rounded-xl border border-red-200 overflow-hidden shadow-sm">
                          <div className="px-4 py-2.5 bg-red-50 border-b border-red-100 flex items-center justify-between">
                            <p className="text-sm font-semibold text-red-800">
                              <AlertTriangle className="h-4 w-4 inline mr-1.5" />
                              Invalid Rows ({validationResults.invalid.length})
                            </p>
                          </div>
                          <div className="overflow-x-auto max-h-60 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">#</th>
                                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Code</th>
                                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Errors</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-red-50">
                                {validationResults.invalid.map((r: any, idx: number) => (
                                  <tr key={idx} className="hover:bg-red-50/50">
                                    <td className="px-3 py-2 text-xs text-gray-500">{r.rowNumber}</td>
                                    <td className="px-3 py-2 text-xs font-mono text-red-700">{r.memberCode || '—'}</td>
                                    <td className="px-3 py-2">
                                      <div className="flex flex-wrap gap-1">
                                        {r.errors.map((err: string, i: number) => (
                                          <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">{err}</span>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Excel Preview + Actions (only if valid rows exist) */}
                      {validationResults.valid.length > 0 && (
                        <>
                          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                            <div className="px-4 py-2.5 border-b bg-gray-50">
                              <p className="text-sm font-semibold text-gray-700">Preview — Ready to Record ({validationResults.valid.length})</p>
                            </div>
                            <div className="overflow-x-auto max-h-64 overflow-y-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">#</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Code</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Name</th>
                                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600">Savings</th>
                                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600">Amount</th>
                                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600">New Total</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {validationResults.valid.map((r: any, idx: number) => (
                                    <tr key={idx} className="hover:bg-gray-50/50">
                                      <td className="px-3 py-2 text-xs text-gray-500">{idx + 1}</td>
                                      <td className="px-3 py-2 text-xs font-mono text-gray-700">{r.memberCode}</td>
                                      <td className="px-3 py-2 text-xs text-gray-800">{r.memberName}</td>
                                      <td className="px-3 py-2 text-xs text-right text-gray-600">{r.currentSavings.toLocaleString()}</td>
                                      <td className="px-3 py-2 text-xs text-right font-semibold text-[#0A9F43]">+{r.amount.toLocaleString()}</td>
                                      <td className="px-3 py-2 text-xs text-right font-bold text-gray-900">{r.newTotal.toLocaleString()}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center justify-between pt-2">
                            <Button variant="outline" size="sm" onClick={() => setIsOpen(false)} className="h-9">
                              Cancel
                            </Button>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                disabled={bulkLoading || validationResults.valid.length === 0}
                                onClick={() => submitBulkEntries(
                                  validationResults.valid,
                                  "Savings",
                                  "Cash",
                                  new Date(),
                                  "",
                                  false
                                )}
                                className="bg-gray-700 hover:bg-gray-800 text-white h-9"
                              >
                                {bulkLoading ? <Loader className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                                Record Bulk
                              </Button>
                              <Button
                                size="sm"
                                disabled={bulkLoading || validationResults.valid.length === 0}
                                onClick={() => submitBulkEntries(
                                  validationResults.valid,
                                  "Savings",
                                  "Cash",
                                  new Date(),
                                  "",
                                  true
                                )}
                                className="bg-[#0A9F43] hover:bg-[#0A9F43]/90 text-white h-9 shadow-sm"
                              >
                                {bulkLoading ? <Loader className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
                                Record & Verify
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

export default function Contributions() {
  const location = useLocation();
  const navigate = useNavigate();
  const [recordToEdit, setRecordToEdit] = useState(undefined);
  const [startInExcelMode, setStartInExcelMode] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const newRecordModal = useModalState();
  const confirmModal = useConfirmModal();
  const verifyModal = useConfirmModal();
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [memberFilterStatus, setMemberFilterStatus] = useState<string>("all");
  const [memberFilterType, setMemberFilterType] = useState<string>("all");
  const [memberFilterPaymentMethod, setMemberFilterPaymentMethod] = useState<string>("all");
  const [startMonth, setStartMonth] = useState<number | null>(null);
  const [startYear, setStartYear] = useState<number | null>(null);
  const [endMonth, setEndMonth] = useState<number | null>(null);
  const [endYear, setEndYear] = useState<number | null>(null);

  // Month/year filtering
  const [yearFilter, setYearFilter] = useState<string>(new Date().getFullYear().toString());
  const [monthFilter, setMonthFilter] = useState<string>((new Date().getMonth() + 1).toString());
  const [memberFilter, setMemberFilter] = useState<string>('');
  const [memberFilterOpen, setMemberFilterOpen] = useState(false);
  const [contributionTypeFilter, setContributionTypeFilter] = useState<string>('');
  const [declareOpen, setDeclareOpen] = useState(false);

  // The savings settings are the source of truth for one share's value.
  // Fetch all contributions for the selected member
  const memberContributionsQuery = useQuery(
    ["member-contributions", selectedContribution?.teacher?.id],
    async () => {
      if (!selectedContribution?.teacher?.id) return { data: [] };
      const { data } = await api.get(`/contributions/teacher/${selectedContribution.teacher.id}`, { params: { limit: 1000 } });
      return data;
    },
    {
      enabled: !!selectedContribution?.teacher?.id && isViewDialogOpen,
      retry: false,
    }
  );

  // Filter contributions
  const filteredMemberContributions = useMemo(() => {
    if (!memberContributionsQuery.data?.data) return [];
    let filtered = [...memberContributionsQuery.data.data];
    
    if (memberFilterStatus !== "all") {
      filtered = filtered.filter((c: any) => {
        const status = c.status || (c.isVerified ? 'Verified' : 'Pending');
        return status === memberFilterStatus;
      });
    }
    
    if (memberFilterType !== "all") {
      filtered = filtered.filter((c: any) => c.contributionType === memberFilterType);
    }
    
    if (memberFilterPaymentMethod !== "all") {
      filtered = filtered.filter((c: any) => c.paymentMethod === memberFilterPaymentMethod);
    }
    
    // Filter by month range
    if (startMonth !== null && startYear !== null) {
      const startDate = startOfMonth(new Date(startYear, startMonth, 1));
      const endDate = (endMonth !== null && endYear !== null) 
        ? endOfMonth(new Date(endYear, endMonth, 1))
        : endOfMonth(new Date(startYear, startMonth, 1)); // If only start is set, filter that month only
      
      filtered = filtered.filter((c: any) => {
        if (!c.contributionDate) return false;
        const contributionDate = new Date(c.contributionDate);
        return contributionDate >= startDate && contributionDate <= endDate;
      });
    } else if (endMonth !== null && endYear !== null) {
      // If only end date is set, filter from beginning of time to end date
      const endDate = endOfMonth(new Date(endYear, endMonth, 1));
      
      filtered = filtered.filter((c: any) => {
        if (!c.contributionDate) return false;
        const contributionDate = new Date(c.contributionDate);
        return contributionDate <= endDate;
      });
    }
    
    return filtered;
  }, [memberContributionsQuery.data?.data, memberFilterStatus, memberFilterType, memberFilterPaymentMethod, startMonth, startYear, endMonth, endYear]);

  // Auto-open modal when accessing /contributions/add route
  useEffect(() => {
    if (location.pathname === "/contributions/add") {
      newRecordModal.setisOpen(true);
      // Update URL to /contributions to avoid confusion
      navigate("/contributions", { replace: true });
    }
  }, [location.pathname, navigate, newRecordModal]);
  // Fetch contributions data
  const recordsQuery = useQuery(
    ["contributions", pageIndex, pageSize, searchText, columnFilters, sorting],
    async () => {
      const params = new URLSearchParams({
        page: (pageIndex + 1).toString(),
        limit: pageSize.toString(),
        search: searchText,
      });

      const { data } = await api.get(`/contributions?${params}`);
      return data;
    },
    {
      keepPreviousData: true,
      retry: 1,
    }
  );

  const allRecordsQuery = useQuery(
    ["contributions-all-for-filters", searchText],
    async () => {
      const { data } = await api.get("/contributions", {
        params: {
          limit: 10000,
          search: searchText,
        },
      });
      return data;
    },
    {
      keepPreviousData: true,
      retry: 1,
    }
  );

  const normalizeContributions = (raw: any) => {
    if (!raw) return [];
    const arr = raw?.results || raw?.data || raw?.contributions || raw?.items || raw || [];
    return Array.isArray(arr) ? arr : [];
  };

  const tableData = useMemo(() => {
    const raw = normalizeContributions(recordsQuery.data);
    return raw.map((item: any) => ({
      ...item,
      contributionDate: item.contributionDate || item.contribution_date || item.createdAt,
      reportingDate: item.salaryMonth || item.salary_month || item.contributionDate || item.contribution_date || item.createdAt,
    }));
  }, [recordsQuery.data]);

  const allContributionData = useMemo(() => {
    const raw = normalizeContributions(allRecordsQuery.data);
    return raw.map((item: any) => ({
      ...item,
      contributionDate: item.contributionDate || item.contribution_date || item.createdAt,
      reportingDate: item.salaryMonth || item.salary_month || item.contributionDate || item.contribution_date || item.createdAt,
    }));
  }, [allRecordsQuery.data]);

  const filterSourceData = allContributionData.length ? allContributionData : tableData;

  const exportContributionsToExcel = () => {
    document.getElementById("export-overview-btn")?.click();
  };

  const availableMonths = [
    { value: "all", label: "All Time" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    const currentYear = new Date().getFullYear();

    for (let year = currentYear + 1; year >= 2000; year -= 1) {
      years.add(year);
    }

    filterSourceData.forEach((contribution: any) => {
      const date = contribution?.reportingDate ? new Date(contribution.reportingDate) : null;
      if (date && !Number.isNaN(date.getTime())) {
        years.add(date.getFullYear());
      }
    });

    const sorted = Array.from(years).sort((a, b) => b - a);
    return [{ value: "all", label: "All Years" }, ...sorted.map(y => ({ value: y.toString(), label: y.toString() }))];
  }, [filterSourceData]);

  const filteredContributions = useMemo(() => {
    return filterSourceData.filter((contribution: any) => {
      const contributionDate = contribution?.reportingDate ? new Date(contribution.reportingDate) : null;
      const matchesYear = contributionDate && !Number.isNaN(contributionDate.getTime()) && yearFilter !== "all"
        ? contributionDate.getFullYear() === parseInt(yearFilter)
        : yearFilter === "all" || (contributionDate && !Number.isNaN(contributionDate.getTime()));
      const matchesMonth = contributionDate && !Number.isNaN(contributionDate.getTime()) && monthFilter !== "all"
        ? contributionDate.getMonth() + 1 === parseInt(monthFilter)
        : monthFilter === "all" || (contributionDate && !Number.isNaN(contributionDate.getTime()));
      const teacher = contribution?.teacher || {};
      const teacherName = teacher.fullName || `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim();
      const teacherCode = teacher.teacherId || teacher.id?.toString() || "";
      const q = memberFilter.trim().toLowerCase();
      const matchesMember = q
        ? teacherName.toLowerCase().includes(q) || teacherCode.toLowerCase().includes(q)
        : true;
      const matchesType = contributionTypeFilter
        ? contribution?.contributionType === contributionTypeFilter
        : true;

      return matchesYear && matchesMonth && matchesMember && matchesType;
    });
  }, [filterSourceData, yearFilter, monthFilter, memberFilter, contributionTypeFilter]);

  const monthlyData = useMemo(() => {
    const q = memberFilter.trim().toLowerCase();
    const memberGroups = filterSourceData.reduce((acc: Record<string, any>, contribution: any) => {
      const contributionDate = contribution?.reportingDate ? new Date(contribution.reportingDate) : null;
      if (!contributionDate || Number.isNaN(contributionDate.getTime())) return acc;
      const matchesYear = yearFilter === "all" || contributionDate.getFullYear() === parseInt(yearFilter);
      const matchesMonth = monthFilter === "all" || contributionDate.getMonth() + 1 === parseInt(monthFilter);
      if (!matchesYear || !matchesMonth) return acc;

      const teacher = contribution?.teacher;
      const memberId = teacher?.id || teacher?.teacherId;
      if (!memberId) return acc;
      const memberName = teacher.fullName ||
        `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() ||
        "Unknown";
      const memberCode = teacher.teacherId || teacher.id?.toString() || "";
      const matchesMember = q
        ? memberName.toLowerCase().includes(q) || memberCode.toLowerCase().includes(q)
        : true;
      const matchesType = contributionTypeFilter
        ? contribution?.contributionType === contributionTypeFilter
        : true;
      if (!matchesMember || !matchesType) return acc;

      if (!acc[memberId]) {
        acc[memberId] = {
          member: teacher,
          totalAmount: 0,
          count: 0,
          verifiedAmount: 0,
          pendingAmount: 0,
        };
      }

      const amount = Number(contribution?.amount || 0);
      acc[memberId].totalAmount += amount;
      acc[memberId].count += 1;

      if (contribution?.isVerified || contribution?.status === "Verified") {
        acc[memberId].verifiedAmount += amount;
      } else {
        acc[memberId].pendingAmount += amount;
      }

      return acc;
    }, {});

    return Object.values(memberGroups).map((group: any) => {
      const memberName = group.member.fullName ||
        `${group.member.firstName || ""} ${group.member.lastName || ""}`.trim() ||
        "Unknown";

      return {
        memberId: group.member.id || group.member.teacherId,
        memberName,
        memberCode: group.member.teacherId || `ID: ${group.member.id}`,
        totalAmount: group.totalAmount,
        count: group.count,
        averageContribution: group.count ? group.totalAmount / group.count : 0,
        verifiedAmount: group.verifiedAmount,
        pendingAmount: group.pendingAmount,
      };
    });
  }, [filterSourceData, yearFilter, monthFilter, memberFilter, contributionTypeFilter]);

  const paginatedFilteredContributions = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredContributions.slice(start, start + pageSize);
  }, [filteredContributions, pageIndex, pageSize]);

  useEffect(() => {
    setPagination((current) => (
      current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }
    ));
  }, [yearFilter, monthFilter, memberFilter, contributionTypeFilter]);

  const totalRecords =
    recordsQuery.data?.pagination?.total ??
    recordsQuery.data?.count ??
    tableData.length;
  const totalPages =
    recordsQuery.data?.pagination?.totalPages ??
    (totalRecords ? Math.ceil(totalRecords / pageSize) : 0);
  const filteredTotalRecords = filteredContributions.length;
  const filteredTotalPages = Math.max(1, Math.ceil(filteredTotalRecords / pageSize));
  const yearlyContributions = filterSourceData.filter((contribution: any) => {
    const contributionDate = contribution?.reportingDate ? new Date(contribution.reportingDate) : null;
    const matchesYear = contributionDate && !Number.isNaN(contributionDate.getTime()) && yearFilter !== "all"
      ? contributionDate.getFullYear() === parseInt(yearFilter)
      : yearFilter === "all" || (contributionDate && !Number.isNaN(contributionDate.getTime()));
    const teacher = contribution?.teacher || {};
    const teacherName = teacher.fullName || `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim();
    const teacherCode = teacher.teacherId || teacher.id?.toString() || "";
    const q = memberFilter.trim().toLowerCase();
    const matchesMember = q
      ? teacherName.toLowerCase().includes(q) || teacherCode.toLowerCase().includes(q)
      : true;
    const matchesType = contributionTypeFilter
      ? contribution?.contributionType === contributionTypeFilter
      : true;

    return matchesYear && matchesMember && matchesType;
  });
  const yearlyTotalAmount = yearlyContributions.reduce(
    (sum, contribution) => sum + Number(contribution?.amount || 0),
    0
  );
  const totalAmountOnPage = filteredContributions.reduce(
    (sum, contribution) => sum + Number(contribution?.amount || 0),
    0
  );
  const verifiedContributionsOnPage = filteredContributions.filter((contribution) => {
    const status = contribution?.status || (contribution?.isVerified ? "Verified" : "Pending");
    return status === "Verified";
  }).length;
  const pendingContributionsOnPage = filteredContributions.filter((contribution) => {
    const status = contribution?.status || (contribution?.isVerified ? "Verified" : "Pending");
    return status === "Pending";
  }).length;
  const totalSharesOnPage = tableData.reduce(
    (sum, contribution) => sum + Number(contribution?.shareCount || 0),
    0
  );

  const formatCurrency = (value: number) => `${value.toLocaleString()} FRW`;

  const columns: ColumnDef<any>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          // @ts-ignore
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px] border-white data-[state=checked]:bg-white data-[state=checked]:text-[#0A9F43]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px] border-gray-400 data-[state=checked]:bg-[#0A9F43] data-[state=checked]:border-[#0A9F43]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "rowNumber",
      header: () => (
        <div className="text-left font-semibold">#</div>
      ),
      cell: ({ row, table }) => {
        const pagination = table.getState().pagination;
        const rowNumber = pagination.pageIndex * pagination.pageSize + row.index + 1;
        return (
          <div className="font-medium text-theme-text">
            {rowNumber}
          </div>
        );
      },
      enableSorting: false,
    },
    {
      id: "memberCode",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Member Code" />
      ),
      accessorFn: (row) => row.teacher?.teacherId || row.teacher?.id?.toString() || "N/A",
      cell: ({ row }) => {
        const contribution = row.original;
        const teacher = contribution.teacher;
        const memberCode = teacher?.teacherId || teacher?.id?.toString() || "N/A";
        return (
          <div className="font-mono text-sm" style={{ color: '#050500' }}>
            {memberCode}
          </div>
        );
      },
    },
    {
      accessorKey: "teacher.fullName",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Member" />
      ),
      cell: ({ row }) => {
        const contribution = row.original;
        const teacher = contribution.teacher;
        return (
          <div 
            className="flex items-center space-x-3 cursor-pointer hover:text-theme-primary transition-colors"
            onClick={() => {
              setSelectedContribution(contribution);
              setIsViewDialogOpen(true);
            }}
            title="Click to view all contributions for this member"
          >
            <div>
              <div className="font-semibold text-theme-text hover:underline">{teacher?.fullName || "Unknown"}</div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "contributionType",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Contribution Type" />
      ),
      cell: ({ row }) => {
        const type = row.getValue("contributionType") as string;
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
            {type || "Monthly"}
          </Badge>
        );
      },
    },
    {
      id: "shares",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Shares" />
      ),
      accessorFn: (row) => row.shareCount || 0,
      cell: ({ row }) => {
        const shareCount = row.original.shareCount;
        return (
          <div>
            {shareCount ? (
              <Badge variant="outline" className="bg-theme-primary/10 text-theme-text border-theme-primary">
                {shareCount} {shareCount === 1 ? 'Share' : 'Shares'}
              </Badge>
            ) : (
              <span className="text-muted-foreground text-sm">0</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "amount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Amount" />
      ),
      filterFn: (row, _id, value) => {
        if (!value || !Array.isArray(value) || value.length === 0) return true;
        const amount = row.original.amount || 0;
        return value.some((v) => {
          if (v === "very_low") return amount >= 0 && amount < 10000;
          if (v === "low") return amount >= 10000 && amount < 50000;
          if (v === "medium") return amount >= 50000 && amount < 100000;
          if (v === "high") return amount >= 100000 && amount < 200000;
          if (v === "very_high") return amount >= 200000;
          return false;
        });
      },
      cell: ({ row }) => {
        const amount = row.getValue("amount") as number;
        return (
          <div className="flex items-center space-x-2">
            <span className="font-semibold text-sm text-theme-text">
              {(amount || 0)?.toLocaleString()} FRW
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "paymentMethod",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Payment Method" />
      ),
      cell: ({ row }) => {
        const method = row.getValue("paymentMethod") as string;
        
        const getMethodColor = (method: string) => {
          switch (method) {
            case "Cash": return "bg-green-100 text-green-800";
            case "Bank Transfer": return "bg-blue-100 text-blue-800";
            case "Mobile Money": return "bg-purple-100 text-purple-800";
            case "Check": return "bg-orange-100 text-orange-800";
            default: return "bg-gray-100 text-gray-800";
          }
        };
        
        return (
          <Badge variant="outline" className={`${getMethodColor(method)} border-0`}>
            <span>{method}</span>
          </Badge>
        );
      },
    },
    {
      id: "source",
      accessorKey: "source",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Source" />
      ),
      cell: ({ row }) => {
        const source = row.getValue("source") as string;
        const isSalaryDeduction = source === "salary_deduction" || !source;
        return (
          <Badge
            variant="outline"
            className={
              isSalaryDeduction
                ? "bg-blue-100 text-blue-800 border-blue-200"
                : "bg-purple-100 text-purple-800 border-purple-200"
            }
          >
            {isSalaryDeduction ? "Salary Deduction" : "Manual Contribution"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "hrApprovedBy",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Approved By" />
      ),
      cell: ({ row }) => {
        const approvedBy = row.original.hrApprovedBy;
        return (
          <div className="text-sm text-theme-text">
            {approvedBy || <span className="text-muted-foreground italic">N/A</span>}
          </div>
        );
      },
    },
    {
      accessorKey: "verifiedBy",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Verified By" />
      ),
      cell: ({ row }) => {
        const verifiedBy = row.original.verifiedBy;
        return (
          <div className="text-sm text-theme-text">
            {verifiedBy || <span className="text-muted-foreground italic">N/A</span>}
          </div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created At" />
      ),
      cell: ({ row }) => {
        const date = row.original.createdAt;
        return (
          <div className="text-sm text-theme-text">
            {date ? format(new Date(date), "MMM d, yyyy HH:mm") : "N/A"}
          </div>
        );
      },
    },
    {
      accessorKey: "updatedAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Last Updated" />
      ),
      cell: ({ row }) => {
        const date = row.original.updatedAt;
        return (
          <div className="text-sm text-theme-text">
            {date ? format(new Date(date), "MMM d, yyyy HH:mm") : "N/A"}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      filterFn: (row, _id, value) => {
        if (!value || !Array.isArray(value) || value.length === 0) return true;
        const status = row.original.status || (row.original.isVerified ? "Verified" : "Pending");
        return value.includes(status);
      },
      cell: ({ row }) => {
        const status = row.original.status || (row.original.isVerified ? "Verified" : "Pending");
        
        const getStatusColor = (status: string) => {
          switch (status) {
            case "Verified": return { bg: '#4dbd07', text: '#ffffff' };
            case "Pending": return { bg: '#F59E0B', text: '#ffffff' };
            case "Rejected": return { bg: '#EF4444', text: '#ffffff' };
            case "Partially Paid": return { bg: '#3B82F6', text: '#ffffff' };
            case "Overpaid": return { bg: '#8B5CF6', text: '#ffffff' };
            case "Reversed": return { bg: '#6B7280', text: '#ffffff' };
            default: return { bg: '#6B7280', text: '#ffffff' };
          }
        };
        
        const colors = getStatusColor(status);
        
        return (
          <div className="flex items-center space-x-2">
            <Badge variant="default" style={{ backgroundColor: colors.bg, color: colors.text, border: 'none' }}>
              {status}
              </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "notes",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Notes" />
      ),
      cell: ({ row }) => {
        const notes = row.getValue("notes") as string;
        return (
          <div className="max-w-[200px]">
            {notes ? (
              <div className="truncate text-sm text-theme-text" title={notes}>
                {notes}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground italic">No notes</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "teacherId",
      header: () => null,
      enableHiding: false,
      accessorFn: (row) => row.teacher?.id?.toString(),
      filterFn: (row, _id, value) => {
        if (!value || !Array.isArray(value) || value.length === 0) return true;
        const teacherId = row.original.teacher?.id?.toString();
        return value.includes(teacherId);
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const contribution = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => {
                  setSelectedContribution(contribution);
                  setIsViewDialogOpen(true);
                }}
              >
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setRecordToEdit(contribution);
                  newRecordModal.setisOpen(true);
                }}
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit Contribution
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  // Copy contribution details to clipboard
                  const details = `${contribution.teacher?.fullName} - ${contribution.amount} FRW - ${format(new Date(contribution.contributionDate), "PPP")}`;
                  navigator.clipboard.writeText(details);
                  toast.success("Contribution details copied to clipboard");
                }}
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy Details
              </DropdownMenuItem>
              {!contribution.isVerified && (
                <DropdownMenuItem
                  onClick={() => {
                    verifyModal.open({
                      meta: {
                        id: contribution.id,
                        name: `${contribution.teacher?.fullName} - ${contribution.amount} FRW`,
                      }
                    });
                  }}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Verify Contribution
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => {
                  confirmModal.open({
                    meta: {
                      id: contribution.id,
                      name: `${contribution.teacher?.fullName} - ${contribution.amount} FRW`,
                    }
                  });
                }}
                className="text-red-600 focus:text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Contribution
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const handleDelete = async (meta) => {
    try {
      await api.delete(`/contributions/${meta.id}`);
      toast.success("Contribution deleted successfully");
      recordsQuery.refetch();
      allRecordsQuery.refetch();
    } catch (error) {
      toast.error("Failed to delete contribution");
    } finally {
      confirmModal.close();
    }
  };

  const handleVerify = async (meta) => {
    verifyModal.setIsLoading(true);
    try {
      await api.patch(`/contributions/${meta.id}/verify`);
      toast.success("Contribution verified successfully");
      recordsQuery.refetch();
      allRecordsQuery.refetch();
    } catch (error) {
      toast.error("Failed to verify contribution");
    } finally {
      verifyModal.close();
    }
  };

  const handleBulkVerify = async (selectedRows) => {
    try {
      const ids = selectedRows.map((row: any) => row.id);
      const { data } = await api.patch("/contributions/bulk/verify", { ids });
      toast.success(data.message || `${ids.length} contribution(s) verified successfully`);
      recordsQuery.refetch();
      allRecordsQuery.refetch();
    } catch (error) {
      toast.error("Failed to verify contributions");
    }
  };

  // Payment method options for filtering
  const paymentMethodOptions = [
    { label: "Cash", value: "Cash" },
    { label: "Bank Transfer", value: "Bank Transfer" },
    { label: "Mobile Money", value: "Mobile Money" },
    { label: "Check", value: "Check" }
  ];

  // Fetch teachers for teacher filter
  const { data: teachersData } = useQuery(
    ["teachers-for-filter"],
    async () => {
      const { data } = await api.get("/teachers");
      return data?.results || data?.data || data || [];
    }
  );

  // Prepare teacher options for filter
  const teacherFilterOptions = teachersData?.map((teacher: any) => ({
    label: teacher.fullName || `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() || `Member ${teacher.id}`,
    value: teacher.id.toString()
  })) || [];

  const memberFilterOptions = useMemo(() => {
    const options = new Map<string, { label: string; value: string; code: string }>();

    (teachersData || []).forEach((teacher: any) => {
      const name = teacher.fullName || `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim();
      const code = teacher.teacherId || teacher.memberCode || teacher.code || teacher.id?.toString() || "";
      if (!name && !code) return;
      const label = [name || `Member ${teacher.id}`, code ? `(${code})` : ""].filter(Boolean).join(" ");
      const value = `${name} ${code}`.trim();
      options.set(`teacher-${teacher.id || code || name}`, { label, value, code });
    });

    filterSourceData.forEach((contribution: any) => {
      const teacher = contribution?.teacher || {};
      const name = teacher.fullName || `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim();
      const code = teacher.teacherId || teacher.memberCode || teacher.code || teacher.id?.toString() || "";
      if (!name && !code) return;
      const label = [name || `Member ${teacher.id}`, code ? `(${code})` : ""].filter(Boolean).join(" ");
      const value = `${name} ${code}`.trim();
      options.set(`contribution-${teacher.id || code || name}`, { label, value, code });
    });

    return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [teachersData, filterSourceData]);

  return (
    <>
      <ConfirmModal
        title="Confirm Verification"
        description={`Are you sure you want to verify the contribution from ${verifyModal.meta?.name}?`}
        meta={verifyModal.meta}
        onConfirm={(meta) => {
          handleVerify(meta);
        }}
        isLoading={verifyModal.isLoading}
        open={verifyModal.isOpen}
        onClose={() => verifyModal.close()}
      />

      <ConfirmModal
        title="Are you sure you want to delete this contribution?"
        description={`This will permanently delete ${confirmModal.meta?.name} and cannot be undone.`}
        meta={confirmModal.meta}
        onConfirm={(meta) => {
          handleDelete(meta);
        }}
        isLoading={confirmModal.isLoading}
        open={confirmModal.isOpen}
        onClose={() => confirmModal.close()}
      />

      <div className="min-h-screen w-full max-w-full overflow-x-hidden" style={{ backgroundColor: "#fffffb" }}>
        <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6 min-w-0">
          {/* Header Section */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1
              className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold tracking-tight shrink-0"
              style={{ color: "#050500" }}
            >
              Contributions
            </h1>

            {/* Action Buttons Toolbar */}
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <Button
                size="sm"
                onClick={() => navigate("/shares/declare")}
                className="bg-[#16A34A] hover:bg-[#15803d] text-white font-semibold rounded-lg shadow-sm transition-all duration-200 cursor-pointer border-0 h-9 px-3 text-xs sm:text-sm"
              >
                <Megaphone className="h-4 w-4 mr-1.5" />
                Declare Monthly Shares
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setStartInExcelMode(true);
                  newRecordModal.setisOpen(true);
                }}
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold rounded-lg shadow-sm transition-all duration-200 cursor-pointer border-0 h-9 px-3 text-xs sm:text-sm"
              >
                <FileUp className="h-4 w-4 mr-1.5" />
                Import Excel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                    window.print();
                  }}
                className="bg-[#DC2626] hover:bg-[#b91c1c] text-white font-semibold rounded-lg shadow-sm transition-all duration-200 cursor-pointer border-0 h-9 px-3 text-xs sm:text-sm"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Export PDF
              </Button>
              <Button
                size="sm"
                onClick={() => {
                    window.print();
                  }}
                className="bg-[#475569] hover:bg-[#334155] text-white font-semibold rounded-lg shadow-sm transition-all duration-200 cursor-pointer border-0 h-9 px-3 text-xs sm:text-sm"
              >
                <Printer className="h-4 w-4 mr-1.5" />
                Print
              </Button>
              <Button
                size="sm"
                onClick={exportContributionsToExcel}
                className="bg-[#059669] hover:bg-[#047857] text-white font-semibold rounded-lg shadow-sm transition-all duration-200 cursor-pointer border-0 h-9 px-3 text-xs sm:text-sm"
              >
                <FileDown className="h-4 w-4 mr-1.5" />
                Export Excel
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              {filteredTotalRecords > 0
                ? `Showing ${monthlyData.length} of ${monthlyData.length} contributions`
                : "No contributions found"}
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-4 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Filters</h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                {/* Year Filter */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Year</label>
                  <Select
                    value={yearFilter}
                    onValueChange={setYearFilter}
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableYears.map((year) => (
                        <SelectItem key={year.value} value={year.value} className="text-xs">
                          {year.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Period Filter */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Period</label>
                  <Select
                    value={monthFilter}
                    onValueChange={setMonthFilter}
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableMonths.map((month) => (
                        <SelectItem key={month.value} value={month.value.toString()} className="text-xs">
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Member Filter */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Member</label>
                  <Popover open={memberFilterOpen} onOpenChange={setMemberFilterOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={memberFilterOpen}
                        className="h-9 w-full justify-between px-3 text-xs font-normal"
                      >
                        <span className={cn("truncate", !memberFilter && "text-muted-foreground")}>
                          {memberFilter
                            ? memberFilterOptions.find((option) => option.value === memberFilter)?.label || memberFilter
                            : "All Members"}
                        </span>
                        <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popper-anchor-width)] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search members..." />
                        <CommandList>
                          <CommandEmpty>No members found.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              value="all members"
                              onSelect={() => {
                                setMemberFilter("");
                                setMemberFilterOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", !memberFilter ? "opacity-100" : "opacity-0")} />
                              All Members
                            </CommandItem>
                            {memberFilterOptions.map((option) => (
                              <CommandItem
                                key={option.value}
                                value={`${option.label} ${option.value}`}
                                onSelect={() => {
                                  setMemberFilter(option.value);
                                  setMemberFilterOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", memberFilter === option.value ? "opacity-100" : "opacity-0")} />
                                <span className="truncate">{option.label}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                
                {/* Contribution Type Filter */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Contribution Type</label>
                  <Select
                    value={contributionTypeFilter || "all"}
                    onValueChange={(value) => setContributionTypeFilter(value === "all" ? "" : value)}
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="text-xs">All Types</SelectItem>
                      {filterSourceData.length > 0 && Array.from(
                        new Set(filterSourceData.map(c => c.contributionType).filter(Boolean))
                      ).map((type) => (
                        <SelectItem key={type} value={type} className="text-xs">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
          </div>

          <MonthlyDeclarationControlCenter
            month={Number(monthFilter) || new Date().getMonth() + 1}
            year={Number(yearFilter) || new Date().getFullYear()}
            onDeclareMonthlyShares={() => setDeclareOpen(true)}
          />
        </div>
      </div>

      <ContributionForm
        isOpen={newRecordModal.isOpen || Boolean(recordToEdit)}
        setIsOpen={(e) => {
          newRecordModal.setisOpen(e);
          if (!e) setStartInExcelMode(false);
          if (!e) {
            setRecordToEdit(undefined);
          }
        }}
        refetch={() => {
          recordsQuery.refetch();
          allRecordsQuery.refetch();
        }}
        record={recordToEdit}
        startInExcelMode={startInExcelMode}
      />

      {/* Contribution Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>All Contributions - {selectedContribution?.teacher?.fullName}</DialogTitle>
            <DialogDescription className="sr-only">
              Contribution history details for {selectedContribution?.teacher?.fullName || "the selected member"}.
            </DialogDescription>
          </DialogHeader>
          {selectedContribution && (
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <Avatar className="h-16 w-16">
                  <AvatarFallback className="text-lg">
                    {selectedContribution.teacher?.firstName?.charAt(0)}{selectedContribution.teacher?.lastName?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-2xl font-bold">{selectedContribution.teacher?.fullName}</h3>
                  <p className="text-muted-foreground">{selectedContribution.teacher?.teacherId}</p>
                  <div className="flex space-x-2 mt-2">
                    <Badge variant="outline">
                      Email: {selectedContribution.teacher?.email || "N/A"}
                    </Badge>
                    <Badge variant="outline">
                      Phone: {selectedContribution.teacher?.phone || "N/A"}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Member Summary Panel */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg bg-green-50 border border-green-100 p-3">
                  <p className="text-xs text-green-700 font-medium">Total Contributed</p>
                  <p className="text-lg font-bold text-green-900 mt-1">
                    {memberContributionsQuery.data?.data?.reduce((sum: number, c: any) => sum + (c.amount || 0), 0)?.toLocaleString() || 0} FRW
                  </p>
                </div>
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                  <p className="text-xs text-blue-700 font-medium">Total Shares</p>
                  <p className="text-lg font-bold text-blue-900 mt-1">
                    {memberContributionsQuery.data?.data?.reduce((sum: number, c: any) => sum + (c.shareCount || 0), 0) || 0}
                  </p>
                </div>
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                  <p className="text-xs text-emerald-700 font-medium">Verified</p>
                  <p className="text-lg font-bold text-emerald-900 mt-1">
                    {memberContributionsQuery.data?.data?.filter((c: any) => (c.status || (c.isVerified ? 'Verified' : 'Pending')) === 'Verified').length || 0}
                  </p>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
                  <p className="text-xs text-amber-700 font-medium">Pending</p>
                  <p className="text-lg font-bold text-amber-900 mt-1">
                    {memberContributionsQuery.data?.data?.filter((c: any) => (c.status || (c.isVerified ? 'Verified' : 'Pending')) === 'Pending').length || 0}
                  </p>
                </div>
              </div>

              <Separator />

              {memberContributionsQuery.isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="h-8 w-8 animate-spin text-theme-primary" />
                    </div>
              ) : memberContributionsQuery.error ? (
                <div className="text-center py-8 text-red-500">
                  Failed to load contributions
                    </div>
              ) : (
                 <div className="space-y-4">
                   <div className="flex justify-between items-center flex-wrap gap-4">
                     <div>
                       <h4 className="text-lg font-semibold">
                         Total Contributions: {filteredMemberContributions.length} / {memberContributionsQuery.data?.data?.length || 0}
                       </h4>
                       <div className="text-sm text-muted-foreground mt-1">
                         Total Amount: {filteredMemberContributions.reduce((sum: number, c: any) => sum + (c.amount || 0), 0)?.toLocaleString() || 0} FRW
                       </div>
                     </div>
                     <div className="flex items-center gap-2">
                       <motion.button
                         onClick={async () => {
                           try {
                             const data = filteredMemberContributions;
                             const memberName = selectedContribution?.teacher?.fullName || "Member";
                             const startDate = format(new Date(), 'MMMM d, yyyy');
                             const endDate = format(new Date(), 'MMMM d, yyyy');
                             
                             const printContent = `
                               <div class="print-header">
                                 <div class="logo-container">
                                   <div class="logo-box">
                                     <img src="${getCooperativeLogoSrc()}" alt="IKIMINA Logo" />
                                   </div>
                                 </div>
                                 <div class="company-info">
                                   <div class="company-name">CAISSE D'ENTRAIDE IPRC KARONGI</div>
                                   <div class="report-title">Contributions Report - ${memberName}</div>
                                   <div class="date-range">${startDate} - ${endDate}</div>
                                 </div>
                               </div>
                               <hr class="separator-thick">
                               <div class="contact-info">
                                 <div><strong>Address:</strong> Karongi, Rwanda</div>
                                 <div><strong>Phone:</strong> +250 783 610 386</div>
                                 <div><strong>Email:</strong> ikiminakarongicollege@gmail.com</div>
                               </div>
                               <hr class="separator-thin">
                               
                               <div style="text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; color: #050500;">
                                 CONTRIBUTIONS REPORT - ${memberName.toUpperCase()}
                               </div>
                               
                               <div class="summary">
                                 <h3>Summary</h3>
                                 <p><strong>Total Contributions:</strong> ${data.reduce((sum: number, c: any) => sum + (c.amount || 0), 0).toLocaleString()} FRW</p>
                                 <p><strong>Verified:</strong> ${data.filter((c: any) => (c.status || (c.isVerified ? 'Verified' : 'Pending')) === 'Verified').length}</p>
                                 <p><strong>Pending:</strong> ${data.filter((c: any) => (c.status || (c.isVerified ? 'Verified' : 'Pending')) === 'Pending').length}</p>
                                 <p><strong>Rejected:</strong> ${data.filter((c: any) => c.status === 'Rejected').length}</p>
                                 <p><strong>Partially Paid:</strong> ${data.filter((c: any) => c.status === 'Partially Paid').length}</p>
                                 <p><strong>Overpaid:</strong> ${data.filter((c: any) => c.status === 'Overpaid').length}</p>
                                 <p><strong>Reversed:</strong> ${data.filter((c: any) => c.status === 'Reversed').length}</p>
                               </div>
                               
                               <table>
                                 <thead>
                                   <tr>
                                     <th>#</th>
                                     <th>Contribution Type</th>
                                     <th>Amount (FRW)</th>
                                     <th>Date</th>
                                     <th>Payment Method</th>
                                     <th>Approved By</th>
                                     <th>Verified By</th>
                                     <th>Created At</th>
                                     <th>Last Updated</th>
                                     <th>Status</th>
                                     <th>Notes</th>
                                   </tr>
                                 </thead>
                                 <tbody>
                                   ${data.map((c: any, index: number) => {
                                     const status = c.status || (c.isVerified ? 'Verified' : 'Pending');
                                     const statusClass = status === 'Verified' ? 'verified' : status === 'Pending' ? 'pending' : status === 'Rejected' ? 'rejected' : status === 'Partially Paid' ? 'partially-paid' : status === 'Overpaid' ? 'overpaid' : 'reversed';
                                     return `
                                     <tr>
                                       <td>${index + 1}</td>
                                       <td>${c.contributionType || 'Monthly'}</td>
                                       <td>${(c.amount || 0).toLocaleString()}${c.shareCount ? ` (${c.shareCount} shares)` : ''}</td>
                                       <td>${c.contributionDate ? format(new Date(c.contributionDate), 'MMM d, yyyy') : 'N/A'}</td>
                                       <td>${c.paymentMethod || '-'}</td>
                                       <td>${c.hrApprovedBy || 'N/A'}</td>
                                       <td>${c.verifiedBy || 'N/A'}</td>
                                       <td>${c.createdAt ? format(new Date(c.createdAt), 'MMM d, yyyy HH:mm') : 'N/A'}</td>
                                       <td>${c.updatedAt ? format(new Date(c.updatedAt), 'MMM d, yyyy HH:mm') : 'N/A'}</td>
                                       <td class="${statusClass}">${status}</td>
                                       <td>${c.notes || '-'}</td>
                                     </tr>
                                   `;
                                   }).join('')}
                                 </tbody>
                               </table>
                               
                               <div class="summary" style="margin-top: 20px;">
                                 <strong>Total Contributions:</strong> ${data.length} | 
                                 <strong>Generated on:</strong> ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}
                               </div>
                             `;
                             
                             const printStyles = `
                               <style id="print-styles">
                                 #print-container {
                                   position: fixed;
                                   left: 0;
                                   top: 0;
                                   width: 100%;
                                   height: 100%;
                                   background: white;
                                   padding: 20px;
                                   z-index: 99999;
                                   overflow: auto;
                                   box-sizing: border-box;
                                 }
                                 #print-container .print-header {
                                   display: flex;
                                   align-items: center;
                                   margin-bottom: 15px;
                                 }
                                 #print-container .logo-container {
                                   margin-right: 20px;
                                 }
                                 #print-container .logo-box {
                                   width: 120px;
                                   display: flex;
                                   flex-direction: column;
                                   align-items: center;
                                 }
                                 #print-container .logo-box img {
                                   max-width: 120px;
                                   height: auto;
                                   object-fit: contain;
                                   border-radius: 50%;
                                 }
                                 #print-container .company-info {
                                   flex: 1;
                                 }
                                 #print-container .company-name {
                                   font-size: 24px;
                                   font-weight: bold;
                                   color: #000;
                                   margin-bottom: 5px;
                                 }
                                 #print-container .report-title {
                                   font-size: 18px;
                                   font-weight: bold;
                                   color: #000;
                                   margin-bottom: 5px;
                                 }
                                 #print-container .date-range {
                                   font-size: 14px;
                                   color: #000;
                                   margin-bottom: 0;
                                 }
                                 #print-container .separator-thick {
                                   border: none;
                                   border-top: 3px solid #000;
                                   margin: 15px 0;
                                   width: 100%;
                                 }
                                 #print-container .separator-thin {
                                   border: none;
                                   border-top: 1px solid #ccc;
                                   margin: 15px 0 20px 0;
                                   width: 100%;
                                 }
                                 #print-container .contact-info {
                                   display: block;
                                   font-size: 12px;
                                   color: #000;
                                   margin-bottom: 15px;
                                 }
                                 #print-container .contact-info div {
                                   display: block;
                                   margin-bottom: 5px;
                                 }
                                 #print-container table {
                                   width: 100%;
                                   border-collapse: collapse;
                                   margin-top: 20px;
                                   font-size: 10px;
                                 }
                                 #print-container th {
                                   background-color: #4dbd07 !important;
                                   color: white !important;
                                   padding: 10px 6px;
                                   text-align: left;
                                   font-weight: 600;
                                   border: 1px solid #3d9a06;
                                 }
                                 #print-container td {
                                   border: 1px solid #ddd;
                                   padding: 8px 6px;
                                   text-align: left;
                                 }
                                 #print-container tbody tr:nth-child(even) {
                                   background-color: #f9f9f9;
                                 }
                                 #print-container .summary {
                                   margin-top: 20px;
                                   padding: 15px;
                                   background-color: #f5f5f5;
                                   border-radius: 5px;
                                   font-size: 14px;
                                 }
                                 #print-container .verified { color: green; font-weight: bold; }
                                 #print-container .pending { color: orange; font-weight: bold; }
                                 #print-container .rejected { color: red; font-weight: bold; }
                                 #print-container .partially-paid { color: blue; font-weight: bold; }
                                 #print-container .overpaid { color: purple; font-weight: bold; }
                                 #print-container .reversed { color: gray; font-weight: bold; }
                                 @media print {
                                   @page {
                                     margin: 1cm;
                                     size: A4;
                                   }
                                   body * {
                                     visibility: hidden;
                                   }
                                   #print-container,
                                   #print-container * {
                                     visibility: visible;
                                   }
                                   #print-container {
                                     position: absolute;
                                     left: 0;
                                     top: 0;
                                     width: 100%;
                                     height: auto;
                                     overflow: visible;
                                   }
                                   #print-container th {
                                     -webkit-print-color-adjust: exact;
                                     print-color-adjust: exact;
                                   }
                                 }
                               </style>
                             `;
                             
                             // Load and convert logo to base64 to avoid reload delays
                               const getBase64Image = (): Promise<string> => {
                                 return new Promise((resolve) => {
                                   const img = new Image();
                                   img.crossOrigin = 'anonymous';
                                   img.onload = () => {
                                     const canvas = document.createElement('canvas');
                                     canvas.width = img.width;
                                     canvas.height = img.height;
                                     const ctx = canvas.getContext('2d');
                                     ctx?.drawImage(img, 0, 0);
                                     const dataURL = canvas.toDataURL('image/jpeg');
                                     resolve(dataURL);
                                   };
                                   img.onerror = () => resolve(getCooperativeLogoSrc());
                                   img.src = getCooperativeLogoSrc();
                                 });
                               };
                               
                               const logoBase64 = await getBase64Image();
                               
                               // Replace logo URL with base64 in print content
                               const printContentWithBase64 = printContent.replace(
                                 /src="\/images\/ikimina-logo\.jpg"/g,
                                 `src="${logoBase64}"`
                               );
                               
                               const existingContainer = document.getElementById('print-container');
                               if (existingContainer) {
                                 existingContainer.remove();
                               }
                               
                               const existingStyles = document.getElementById('print-styles');
                               if (existingStyles) {
                                 existingStyles.remove();
                               }
                               
                               document.head.insertAdjacentHTML('beforeend', printStyles);
                               
                               const printContainer = document.createElement('div');
                               printContainer.id = 'print-container';
                               printContainer.className = 'print-container';
                               printContainer.innerHTML = printContentWithBase64;
                               document.body.appendChild(printContainer);
                               
                               // Wait for content to render, then trigger print
                               setTimeout(() => {
                                 window.print();
                                 
                                 setTimeout(() => {
                                   if (printContainer && printContainer.parentNode) {
                                     printContainer.remove();
                                   }
                                   const styles = document.getElementById('print-styles');
                                   if (styles) {
                                     styles.remove();
                                   }
                                 }, 500);
                               }, 300);
                           } catch (error) {
                             console.error("Print error:", error);
                             toast.error("Failed to print report");
                           }
                         }}
                         className="h-8 sm:h-9 rounded-md px-2 sm:px-3 text-xs sm:text-sm font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 border-2"
                         style={{ 
                           backgroundColor: "transparent",
                           borderColor: "#4dbd07",
                           color: "#050500",
                         }}
                         whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                         whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                       >
                         <Printer className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                         Print
                       </motion.button>
                       <motion.button
                         onClick={async () => {
                           try {
                             const data = filteredMemberContributions;
                             const memberName = selectedContribution?.teacher?.fullName || "Member";
                             const jsPDF = (await import('jspdf')).jsPDF;
                             const html2canvas = (await import('html2canvas')).default;
                             
                             const pdfDiv = document.createElement('div');
                             pdfDiv.style.width = '1400px';
                             pdfDiv.style.padding = '20px';
                             pdfDiv.style.backgroundColor = 'white';
                             pdfDiv.style.fontFamily = 'Arial, sans-serif';
                             
                             pdfDiv.innerHTML = `
                               <div style="display: flex; align-items: center; margin-bottom: 15px;">
                                 <div style="margin-right: 20px;">
                                   <div style="width: 120px; display: flex; flex-direction: column; align-items: center;">
                                     <img src="${getCooperativeLogoSrc()}" alt="IKIMINA Logo" style="max-width: 120px; height: auto; object-fit: contain; border-radius: 50%;" />
                                   </div>
                                 </div>
                                 <div style="flex: 1;">
                                   <div style="font-size: 24px; font-weight: bold; color: #000; margin-bottom: 5px;">CAISSE D'ENTRAIDE IPRC KARONGI</div>
                                   <div style="font-size: 18px; font-weight: bold; color: #000; margin-bottom: 5px;">Contributions Report - ${memberName}</div>
                                   <div style="font-size: 14px; color: #000;">${format(new Date(), 'MMMM d, yyyy')}</div>
                                 </div>
                               </div>
                               <hr style="border: none; border-top: 3px solid #000; margin: 15px 0; width: 100%;">
                               <div style="display: block; font-size: 12px; color: #000; margin-bottom: 15px;">
                                 <div><strong>Address:</strong> Karongi, Rwanda</div>
                                 <div><strong>Phone:</strong> +250 783 610 386</div>
                                 <div><strong>Email:</strong> ikiminakarongicollege@gmail.com</div>
                               </div>
                               <hr style="border: none; border-top: 1px solid #ccc; margin: 15px 0 20px 0; width: 100%;">
                               
                               <div style="text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; color: #050500;">
                                 CONTRIBUTIONS REPORT - ${memberName.toUpperCase()}
                               </div>
                               
                               <div style="margin-top: 20px; padding: 15px; background-color: #f5f5f5; border-radius: 5px; font-size: 14px;">
                                 <h3 style="margin-bottom: 10px;">Summary</h3>
                                 <p><strong>Total Contributions:</strong> ${data.reduce((sum: number, c: any) => sum + (c.amount || 0), 0).toLocaleString()} FRW</p>
                                 <p><strong>Verified:</strong> ${data.filter((c: any) => (c.status || (c.isVerified ? 'Verified' : 'Pending')) === 'Verified').length}</p>
                                 <p><strong>Pending:</strong> ${data.filter((c: any) => (c.status || (c.isVerified ? 'Verified' : 'Pending')) === 'Pending').length}</p>
                                 <p><strong>Rejected:</strong> ${data.filter((c: any) => c.status === 'Rejected').length}</p>
                                 <p><strong>Partially Paid:</strong> ${data.filter((c: any) => c.status === 'Partially Paid').length}</p>
                                 <p><strong>Overpaid:</strong> ${data.filter((c: any) => c.status === 'Overpaid').length}</p>
                                 <p><strong>Reversed:</strong> ${data.filter((c: any) => c.status === 'Reversed').length}</p>
                               </div>
                               
                               <table style="width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 20px;">
                                 <thead>
                                   <tr>
                                     <th style="background-color: #4dbd07; color: white; padding: 10px 4px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">#</th>
                                     <th style="background-color: #4dbd07; color: white; padding: 10px 4px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Type</th>
                                     <th style="background-color: #4dbd07; color: white; padding: 10px 4px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Amount</th>
                                     <th style="background-color: #4dbd07; color: white; padding: 10px 4px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Date</th>
                                     <th style="background-color: #4dbd07; color: white; padding: 10px 4px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Payment</th>
                                     <th style="background-color: #4dbd07; color: white; padding: 10px 4px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Approved By</th>
                                     <th style="background-color: #4dbd07; color: white; padding: 10px 4px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Verified By</th>
                                     <th style="background-color: #4dbd07; color: white; padding: 10px 4px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Created At</th>
                                     <th style="background-color: #4dbd07; color: white; padding: 10px 4px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Updated</th>
                                     <th style="background-color: #4dbd07; color: white; padding: 10px 4px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Status</th>
                                     <th style="background-color: #4dbd07; color: white; padding: 10px 4px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Notes</th>
                                   </tr>
                                 </thead>
                                 <tbody>
                                   ${data.map((c: any, index: number) => {
                                     const status = c.status || (c.isVerified ? 'Verified' : 'Pending');
                                     const statusColor = status === 'Verified' ? 'green' : status === 'Pending' ? 'orange' : status === 'Rejected' ? 'red' : status === 'Partially Paid' ? 'blue' : status === 'Overpaid' ? 'purple' : 'gray';
                                     return `
                                     <tr style="${index % 2 === 0 ? 'background-color: #f9f9f9;' : ''}">
                                       <td style="border: 1px solid #ddd; padding: 6px 4px;">${index + 1}</td>
                                       <td style="border: 1px solid #ddd; padding: 6px 4px;">${c.contributionType || 'Monthly'}</td>
                                       <td style="border: 1px solid #ddd; padding: 6px 4px;">${(c.amount || 0).toLocaleString()}${c.shareCount ? ` (${c.shareCount} shares)` : ''}</td>
                                       <td style="border: 1px solid #ddd; padding: 6px 4px;">${c.contributionDate ? format(new Date(c.contributionDate), 'MMM d, yyyy') : 'N/A'}</td>
                                       <td style="border: 1px solid #ddd; padding: 6px 4px;">${c.paymentMethod || '-'}</td>
                                       <td style="border: 1px solid #ddd; padding: 6px 4px;">${c.hrApprovedBy || 'N/A'}</td>
                                       <td style="border: 1px solid #ddd; padding: 6px 4px;">${c.verifiedBy || 'N/A'}</td>
                                       <td style="border: 1px solid #ddd; padding: 6px 4px;">${c.createdAt ? format(new Date(c.createdAt), 'MMM d, yyyy HH:mm') : 'N/A'}</td>
                                       <td style="border: 1px solid #ddd; padding: 6px 4px;">${c.updatedAt ? format(new Date(c.updatedAt), 'MMM d, yyyy HH:mm') : 'N/A'}</td>
                                       <td style="border: 1px solid #ddd; padding: 6px 4px; color: ${statusColor}; font-weight: bold;">${status}</td>
                                       <td style="border: 1px solid #ddd; padding: 6px 4px;">${c.notes || '-'}</td>
                                     </tr>
                                   `;
                                   }).join('')}
                                 </tbody>
                               </table>
                               
                               <div style="margin-top: 24px; padding: 15px; background-color: #f5f5f5; border-radius: 5px; font-size: 14px;">
                                 <strong>Total Contributions:</strong> ${data.length} | 
                                 <strong>Generated on:</strong> ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}
                               </div>
                             `;
                             document.body.appendChild(pdfDiv);
                             
                             const canvas = await html2canvas(pdfDiv, { scale: 2 });
                             const imgData = canvas.toDataURL('image/png');
                             const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [canvas.width, canvas.height] });
                             pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
                             pdf.save(`contributions_${memberName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
                             document.body.removeChild(pdfDiv);
                             
                             toast.success('PDF report downloaded successfully');
                           } catch (error) {
                             console.error("PDF error:", error);
                             toast.error("Failed to generate PDF report");
                           }
                         }}
                         className="h-8 sm:h-9 rounded-md px-2 sm:px-3 text-xs sm:text-sm font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 border-2"
                         style={{ 
                           backgroundColor: "transparent",
                           borderColor: "#4dbd07",
                           color: "#050500",
                         }}
                         whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                         whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                       >
                         <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                         PDF
                       </motion.button>
                     </div>
                   </div>
                  
                  {/* Filters */}
                  <div className="flex flex-wrap gap-2 items-center">
                    <Select value={memberFilterStatus} onValueChange={setMemberFilterStatus}>
                      <SelectTrigger className="w-[150px] h-9">
                        <SelectValue placeholder="Filter by Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Verified">Verified</SelectItem>
                        <SelectItem value="Rejected">Rejected</SelectItem>
                        <SelectItem value="Partially Paid">Partially Paid</SelectItem>
                        <SelectItem value="Overpaid">Overpaid</SelectItem>
                        <SelectItem value="Reversed">Reversed</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={memberFilterType} onValueChange={setMemberFilterType}>
                      <SelectTrigger className="w-[150px] h-9">
                        <SelectValue placeholder="Filter by Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="Monthly">Monthly</SelectItem>
                        <SelectItem value="Weekly">Weekly</SelectItem>
                        <SelectItem value="One-time">One-time</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={memberFilterPaymentMethod} onValueChange={setMemberFilterPaymentMethod}>
                      <SelectTrigger className="w-[150px] h-9">
                        <SelectValue placeholder="Filter by Payment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Methods</SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                        <SelectItem value="Mobile Money">Mobile Money</SelectItem>
                        <SelectItem value="Check">Check</SelectItem>
                        <SelectItem value="Salary Deduction">Salary Deduction</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Month Range Filter */}
                    <div className="flex items-center gap-2 border rounded-md px-2 py-1 h-9">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">From:</span>
                      <Select 
                        value={startMonth !== null ? startMonth.toString() : "all"} 
                        onValueChange={(value) => setStartMonth(value === "all" ? null : parseInt(value))}
                      >
                        <SelectTrigger className="w-[100px] h-7 border-0 p-0">
                          <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {Array.from({ length: 12 }, (_, i) => {
                            const date = new Date(2000, i, 1);
                            return (
                              <SelectItem key={i} value={i.toString()}>
                                {format(date, "MMM")}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <Select 
                        value={startYear !== null ? startYear.toString() : "all"} 
                        onValueChange={(value) => setStartYear(value === "all" ? null : parseInt(value))}
                      >
                        <SelectTrigger className="w-[80px] h-7 border-0 p-0">
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {Array.from({ length: 6 }, (_, i) => {
                            const year = new Date().getFullYear() - i;
                            return (
                              <SelectItem key={year} value={year.toString()}>
                                {year}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">To:</span>
                      <Select 
                        value={endMonth !== null ? endMonth.toString() : "all"} 
                        onValueChange={(value) => setEndMonth(value === "all" ? null : parseInt(value))}
                      >
                        <SelectTrigger className="w-[100px] h-7 border-0 p-0">
                          <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {Array.from({ length: 12 }, (_, i) => {
                            const date = new Date(2000, i, 1);
                            return (
                              <SelectItem key={i} value={i.toString()}>
                                {format(date, "MMM")}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <Select 
                        value={endYear !== null ? endYear.toString() : "all"} 
                        onValueChange={(value) => setEndYear(value === "all" ? null : parseInt(value))}
                      >
                        <SelectTrigger className="w-[80px] h-7 border-0 p-0">
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {Array.from({ length: 6 }, (_, i) => {
                            const year = new Date().getFullYear() - i;
                            return (
                              <SelectItem key={year} value={year.toString()}>
                                {year}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {(memberFilterStatus !== "all" || memberFilterType !== "all" || memberFilterPaymentMethod !== "all" || 
                      startMonth !== null || startYear !== null || endMonth !== null || endYear !== null) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setMemberFilterStatus("all");
                          setMemberFilterType("all");
                          setMemberFilterPaymentMethod("all");
                          setStartMonth(null);
                          setStartYear(null);
                          setEndMonth(null);
                          setEndYear(null);
                        }}
                        className="h-9"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[50px]">#</TableHead>
                            <TableHead>Contribution Type</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead className="w-[140px] min-w-[140px]">Date</TableHead>
                            <TableHead>Payment Method</TableHead>
                            <TableHead>Approved By</TableHead>
                            <TableHead>Verified By</TableHead>
                            <TableHead>Created At</TableHead>
                            <TableHead>Last Updated</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Notes</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredMemberContributions.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                                No contributions found
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredMemberContributions.map((contribution: any, index: number) => {
                              const status = contribution.status || (contribution.isVerified ? 'Verified' : 'Pending');
                              const getStatusColor = (status: string) => {
                                switch (status) {
                                  case "Verified": return { bg: '#4dbd07', text: '#ffffff' };
                                  case "Pending": return { bg: '#F59E0B', text: '#ffffff' };
                                  case "Rejected": return { bg: '#EF4444', text: '#ffffff' };
                                  case "Partially Paid": return { bg: '#3B82F6', text: '#ffffff' };
                                  case "Overpaid": return { bg: '#8B5CF6', text: '#ffffff' };
                                  case "Reversed": return { bg: '#6B7280', text: '#ffffff' };
                                  default: return { bg: '#6B7280', text: '#ffffff' };
                                }
                              };
                              const colors = getStatusColor(status);
                              
                              return (
                                <TableRow key={contribution.id}>
                                  <TableCell className="font-medium">{index + 1}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
                                      {contribution.contributionType || "Monthly"}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex flex-col">
                                      <span className="font-semibold">{(contribution.amount || 0)?.toLocaleString()} FRW</span>
                                      {contribution.shareCount && (
                                        <span className="text-xs text-muted-foreground">{contribution.shareCount} shares</span>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="w-[140px] min-w-[140px]">
                                    {contribution.contributionDate ? format(new Date(contribution.contributionDate), "MMM d, yyyy") : "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline">{contribution.paymentMethod || "N/A"}</Badge>
                                  </TableCell>
                                  <TableCell className="text-sm">{contribution.hrApprovedBy || "N/A"}</TableCell>
                                  <TableCell className="text-sm">{contribution.verifiedBy || "N/A"}</TableCell>
                                  <TableCell className="text-sm">
                                    {contribution.createdAt ? format(new Date(contribution.createdAt), "MMM d, yyyy HH:mm") : "N/A"}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {contribution.updatedAt ? format(new Date(contribution.updatedAt), "MMM d, yyyy HH:mm") : "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    <Badge 
                                      variant="default" 
                                      style={{ backgroundColor: colors.bg, color: colors.text, border: 'none' }}
                                    >
                                      {status}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="max-w-[200px]">
                                    <div className="truncate" title={contribution.notes || ""}>
                                      {contribution.notes || "N/A"}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
